/**
 * EPL Cart - Lean Cart Implementation
 * Simplified cart functionality without overengineering
 * Vanilla JavaScript, ES6+, no jQuery dependency
 */

// Prevent multiple script executions
(function() {
  'use strict';
  
if (window.__EPL_CART_LOADED__) {
  return; // Script already loaded, skip execution
}
window.__EPL_CART_LOADED__ = true;

/**
 * Whether an add-on cart line belongs to the main product being removed/queried.
 *
 * The main product's _uuid is handle-based and CHAINED as add-ons are added — suffixes are
 * appended: "iphone-17-..." -> "iphone-17-...-applecare-..." -> "...-belkin-...". AppleCare and
 * Second Warranty get cross-synced to the final chained value (see _crossSyncAddonUuid), but
 * Vegglader (charger) and GWP are NOT — they keep the un-chained base _uuid, which is a PREFIX
 * of the main's final _uuid. A strict `addonUuid === mainUuid` check therefore drops them, so
 * removing the main leaves the charger/GWP behind. Tolerate the chain-prefix relationship.
 *
 * Callers already gate on the per-add-on SKU (`_*_main_product_sku === mainSku`), which keeps
 * this scoped to a single main product — the prefix only relaxes matching within that product.
 */
function addonUuidMatchesMain(addonUuid, mainUuid) {
  if (!mainUuid) return true;            // no uuid to disambiguate against → match by SKU alone (caller's gate)
  if (!addonUuid) return false;          // preserve original behaviour (a set mainUuid never matched a blank addon uuid)
  // ONE-DIRECTIONAL on purpose: this answers "does this add-on line belong to
  // this main line?" using fresh cart data, where the add-on's uuid is always
  // either EQUAL to the main's (AC+/SW are cross-synced to the chained value) or
  // a BASE PREFIX of it (Vegglader/GWP keep the un-chained base). It must NOT be
  // bidirectional: a chained add-on uuid "starts with" a shorter base uuid, so a
  // relaxed check would match an add-on belonging to a chained instance against a
  // DIFFERENT bare same-SKU instance and cascade-remove the wrong add-on.
  return addonUuid === mainUuid || mainUuid.startsWith(addonUuid);
}

/**
 * Whether two _uuid values could refer to the same main-product instance, used
 * ONLY to LOCATE a main line by a possibly-STALE search uuid (see _findMainProduct).
 *
 * The main product's _uuid is CHAINED as add-ons are added (e.g. "iphone-17" →
 * "iphone-17-applecare-...") and REVERTED toward the base as they are removed.
 * A cached/stale DOM uuid can therefore be EITHER longer (chained) OR shorter
 * (reverted) than the live line's uuid. A one-directional `item.startsWith(search)`
 * check silently fails on the reverted case — which made `_findMainProduct` return
 * null after an add-on removal and left cached keys stale. Tolerate the prefix
 * relationship in BOTH directions. The caller always gates on SKU too.
 *
 * Do NOT use this for add-on→main membership tests — see addonUuidMatchesMain.
 */
function uuidsRelated(a, b) {
  if (!a || !b) return false;
  return a === b || a.startsWith(b) || b.startsWith(a);
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Debounce utility for input handlers
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Standardized fetch configuration helper
 */
function getFetchConfig(method = 'GET', body = null) {
  const config = {
    method,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  };
  if (body) {
    config.body = JSON.stringify(body);
  }
  return config;
}

/**
 * Google Analytics tracking helper for cart events
 * Tracks add_to_cart and remove_from_cart events
 */
function trackCartEvent(eventType, item, currency, value) {
  if (typeof dataLayer === 'undefined') return;
  
  // Clear previous ecommerce data
  dataLayer.push({ 'ecommerce': null });
  
  // Push new event
  dataLayer.push({
    'event': eventType,
    'ecommerce': {
      'currency': currency || (window.themeJsData?.shopCurrency) || 'NOK',
      'value': value || 0,
      'items': [{
        'item_id': item.sku || item.variant_id || item.id,
        'item_name': item.product_title || item.title || '',
        'quantity': item.quantity || 1,
        'price': ((item.final_price || item.price || 0) / 100).toFixed(2)
      }]
    }
  });
}

/**
 * Format money from cents to currency string
 * @param {number} cents - Price in cents
 * @param {string} currency - Currency code (e.g., 'NOK', 'USD')
 * @returns {string} Formatted price string
 */
function formatMoney(cents, currency = 'NOK') {
  const amount = cents / 100;
  
  // Try to use Intl.NumberFormat for proper currency formatting
  try {
    const locale = currency === 'NOK' ? 'no-NO' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  } catch (e) {
    // Fallback: simple formatting
    return `${amount.toFixed(2)} ${currency}`;
  }
}

// EPLE-27: writes a cart.js amount into a price element. Punchout and B2B customers see prices
// ex VAT: layout/theme.liquid sets window.eplShowPricesExVat and converts the server-rendered
// prices, so a price written here is converted the same way instead of flashing gross first.
function writePrice(el, cents, currency = 'NOK') {
  el.dataset.eplCents = String(cents);
  if (window.eplShowPricesExVat) {
    const exVatCents = Math.round(cents / 1.25 / 100) * 100;
    el.innerHTML = `${formatMoney(exVatCents, currency).replace(/[\u00A0\u202F]/g, ' ')} <span class="exvat-text">eks. mva.</span>`;
    el.classList.add('exvat-processed');
  } else {
    el.textContent = formatMoney(cents, currency);
  }
}

// ============================================================================
// Quantity Input Custom Element (EPL version)
// ============================================================================

class QuantityInputEpl extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Prevent duplicate event listener attachment
    if (this._listenersAttached) {
      return;
    }

    this.input = this.querySelector('input');
    // Support both old and new class names for buttons
    this.minusButton = this.querySelector('button[name="minus"]') || this.querySelector('.epl-qty-button[name="minus"]');
    this.plusButton = this.querySelector('button[name="plus"]') || this.querySelector('.epl-qty-button[name="plus"]');

    // Set initial disabled state for minus button
    if (this.minusButton && this.input) {
      const currentValue = parseInt(this.input.value) || 1;
      this.minusButton.disabled = currentValue <= 1;
      this.minusButton.classList.toggle('disabled', currentValue <= 1);
    }

    if (this.minusButton) {
      this.minusButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.changeQuantity(-1);
      });
    }
    if (this.plusButton) {
      this.plusButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.changeQuantity(1);
      });
    }
    if (this.input) {
      this.input.addEventListener('change', this.handleChange.bind(this));
    }
    
    // Mark listeners as attached to prevent duplicates
    this._listenersAttached = true;
  }
  
  disconnectedCallback() {
    // Reset flag when element is removed from DOM
    this._listenersAttached = false;
  }

  changeQuantity(delta) {
    if (!this.input) return;
    const isAddOn = this.input.hasAttribute('data-addon-type');
    const currentValue = parseInt(this.input.value) || (isAddOn ? 0 : 1);
    const minValue = isAddOn ? 0 : 1;
    const newValue = Math.max(minValue, currentValue + delta);
    this.input.value = newValue;
    // Update button disabled state
    if (this.minusButton) {
      this.minusButton.disabled = newValue <= minValue;
      this.minusButton.classList.toggle('disabled', newValue <= minValue);
    }
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleChange() {
    if (!this.input) return;
    
    const isAddOn = this.input.hasAttribute('data-addon-type');
    const minValue = isAddOn ? 0 : 1;
    const value = parseInt(this.input.value) || minValue;
    
    // Ensure minimum quantity (0 for add-ons, 1 for main products)
    if (value < minValue) {
      this.input.value = minValue;
    }
    
    // Update minus button state
    if (this.minusButton) {
      this.minusButton.disabled = value <= minValue;
      this.minusButton.classList.toggle('disabled', value <= minValue);
    }
    
    const cartItems = this.closest('cart-items-epl');
    if (cartItems) {
      if (isAddOn) {
        cartItems.handleAddOnQuantityChange(this.input);
      } else {
        cartItems.handleQuantityChange(this.input);
      }
    }
  }
}

if (!customElements.get('quantity-input-epl')) {
  customElements.define('quantity-input-epl', QuantityInputEpl);
}

// ============================================================================
// Main Cart Items Custom Element
// ============================================================================

class CartItemsEpl extends HTMLElement {
  constructor() {
    super();
    // Create debounced update functions for quantity changes
    this.debouncedUpdateQuantity = debounce(this.updateQuantityDebounced.bind(this), 500);
    this.debouncedUpdateQuantityOptimistic = debounce(this.updateQuantityOptimisticDebounced.bind(this), 500);

    // Cart data cache for optimistic updates
    this._cartDataCache = null;
    this._cartDataCacheTime = 0;

    // Operation lock to prevent race conditions from rapid clicks
    this._cartOperationInProgress = false;
    this._pendingOperations = [];

    this.setupEventListeners();
  }

  // ============================================================================
  // Operation Lock (prevents race conditions from rapid clicks)
  // ============================================================================

  /**
   * Execute cart operation with lock to prevent race conditions
   * Operations are queued and executed serially. Queued operations
   * get fresh cart data before executing to ensure valid item keys.
   * @param {Function} operation - Async function to execute
   * @returns {Promise} Result of the operation
   */
  async executeWithLock(operation) {
    // If operation in progress, queue and wait
    if (this._cartOperationInProgress) {
      return new Promise((resolve, reject) => {
        this._pendingOperations.push({
          operation,
          resolve,
          reject,
          needsFreshData: true
        });
      });
    }

    this._cartOperationInProgress = true;
    try {
      const result = await operation();
      return result;
    } finally {
      // Process next queued operation BEFORE releasing lock to prevent race window
      if (this._pendingOperations.length > 0) {
        const next = this._pendingOperations.shift();
        // Refresh cart data for queued operations to get current item keys
        try {
          if (next.needsFreshData) {
            await this.getCartData(true);
          }
          // Execute next operation while still holding lock
          // The recursive call sees lock is held and the operation runs directly
          // because we're already inside the lock context
          next.operation().then(next.resolve).catch(next.reject).finally(() => {
            // Release lock and process next after this operation completes
            this._processNextOperation();
          });
        } catch (e) {
          // getCartData failed - reject this operation and move to next
          next.reject(e);
          this._processNextOperation();
        }
      } else {
        // No pending operations, release lock
        this._cartOperationInProgress = false;
      }
    }
  }

  /**
   * Process next operation in queue, or release lock if queue empty
   * @private
   */
  _processNextOperation() {
    if (this._pendingOperations.length > 0) {
      const next = this._pendingOperations.shift();
      (async () => {
        try {
          if (next.needsFreshData) {
            await this.getCartData(true);
          }
          next.operation().then(next.resolve).catch(next.reject).finally(() => {
            this._processNextOperation();
          });
        } catch (e) {
          next.reject(e);
          this._processNextOperation();
        }
      })();
    } else {
      this._cartOperationInProgress = false;
    }
  }

  connectedCallback() {
    // Setup addon toggles and upsell buttons when element is connected
    this.setupAddonToggles();
    this.setupUpsellButtons();
  }

  setupEventListeners() {
    // Event listeners are handled by quantity-input-epl custom elements
  }

  // ============================================================================
  // Cart Data Caching (Phase 1.2)
  // ============================================================================

  /**
   * Get cart data with caching to reduce API calls
   * @param {boolean} forceRefresh - Force refresh from API
   * @returns {Promise<Object>} Cart data
   */
  async getCartData(forceRefresh = false) {
    const now = Date.now();
    const cacheAge = now - this._cartDataCacheTime;
    const cacheValid = !forceRefresh && this._cartDataCache && cacheAge < 5000;

    if (cacheValid) {
      return this._cartDataCache;
    }

    const cartData = await fetch(`${routes.cart_url}.js`).then(r => r.json());
    this._cartDataCache = cartData;
    this._cartDataCacheTime = now;
    return cartData;
  }

  /**
   * Invalidate the cart data cache
   */
  invalidateCartCache() {
    this._cartDataCache = null;
    this._cartDataCacheTime = 0;
  }

  /**
   * Update cart cache with new data
   * @param {Object} cartData - New cart data
   */
  updateCartCache(cartData) {
    this._cartDataCache = cartData;
    this._cartDataCacheTime = Date.now();
  }

  // ============================================================================
  // Back-link Property Helpers (AC+ / SW)
  // ============================================================================

  /**
   * Find the main product in a cart items array by key (with SKU+UUID fallback).
   * @param {Array} items - Cart items array
   * @param {Object} opts - { mainItemKey, mainProductSku, mainProductUuid }
   * @returns {Object|null} The main product cart item, or null
   */
  _findMainProduct(items, { mainItemKey, mainProductSku, mainProductUuid }) {
    if (mainItemKey) {
      const byKey = items.find(item => item.key === mainItemKey);
      if (byKey) return byKey;
    }
    // Fallback: key may be stale after property changes — find by SKU + UUID
    return items.find(item =>
      item.sku === mainProductSku &&
      !item.properties?._aplcr_main_product_sku &&
      !item.properties?._sw_main_product_sku &&
      !item.properties?._gwp_main_product_sku &&
      !item.properties?._vegglader_main_product_sku &&
      item.product_type !== 'AppleCare' &&
      item.product_type !== 'SecondWarranty' &&
      (!mainProductUuid || uuidsRelated(item.properties?._uuid, mainProductUuid))
    ) || null;
  }

  /**
   * Resolve the live cart line for a main-product row whose cached data-item-key
   * may be stale, and self-heal the cached keys in the DOM.
   *
   * The line-item key rotates whenever a line's properties change, and every
   * add-on toggle mutates the main product's _uuid + back-link props. So a key
   * cached in the DOM at render time can point at a line that no longer exists.
   * Looking it up by the cached key alone then silently no-ops the operation
   * until a full re-render — the root cause of the "first Fjern does nothing" bug.
   *
   * Resolves by cached key first, then falls back to a stable SKU+UUID match,
   * and rewrites the cached keys on the container + remove button so later
   * operations hit the right line.
   *
   * @param {HTMLElement} cartItem - the .epl-cart-item container
   * @param {string} cachedKey - the data-item-key currently cached in the DOM
   * @param {Object} cartData - fresh cart data ({ items: [...] })
   * @returns {Object|null} the resolved cart line item, or null
   */
  _resolveMainItem(cartItem, cachedKey, cartData) {
    const items = cartData?.items || [];
    let item = cachedKey ? items.find(i => i.key === cachedKey) : null;

    if (!item && cartItem) {
      const sku = cartItem.dataset.sku || '';
      // The container carries no UUID attribute; borrow it from a child add-on
      // option (present whenever the main product has add-on offers) to
      // disambiguate multiple lines of the same SKU.
      const uuid = cartItem.querySelector('.epl-addon-option')?.dataset.mainProductUuid || '';
      if (sku && uuid) {
        item = this._findMainProduct(items, { mainProductSku: sku, mainProductUuid: uuid });
      } else if (sku) {
        // No UUID to disambiguate — only trust a SKU match when it is unique.
        // Guessing among duplicate same-SKU main lines could remove the wrong
        // product, so bail (the caller safely skips + re-renders) when ambiguous.
        const matches = items.filter(i =>
          i.sku === sku &&
          !i.properties?._aplcr_main_product_sku &&
          !i.properties?._sw_main_product_sku &&
          !i.properties?._gwp_main_product_sku &&
          !i.properties?._vegglader_main_product_sku &&
          i.product_type !== 'AppleCare' &&
          i.product_type !== 'SecondWarranty'
        );
        if (matches.length === 1) item = matches[0];
      }
    }

    if (item && cartItem && item.key !== cachedKey) {
      cartItem.dataset.itemKey = item.key;
      const removeButton = cartItem.querySelector('cart-remove-button-epl');
      if (removeButton) removeButton.dataset.itemKey = item.key;
    }

    return item || null;
  }

  /**
   * Add back-link properties to the main product and add the addon to cart.
   * Handles chained UUID, cross-sync with sibling addon, and the preliminary-add
   * fallback when addon handle is unknown.
   *
   * @param {Object} opts
   * @param {string} opts.variantId - Addon variant ID
   * @param {number} opts.quantity - Quantity to add
   * @param {string} opts.addonType - 'applecare' | 'secondwarranty'
   * @param {string} opts.mainProductSku
   * @param {string} opts.mainProductHandle
   * @param {Object} opts.mainItem - Main product cart item
   * @param {string} opts.currentUuid - Current UUID of the main product
   * @param {string|null} opts.addonSku - Addon SKU (may be null)
   * @param {string|null} opts.addonHandle - Addon handle (may be null)
   */
  async _addBackLinkedAddon({ variantId, quantity, addonType, mainProductSku, mainProductHandle, mainItem, currentUuid, addonSku, addonHandle, sections = null }) {
    let resolvedHandle = addonHandle;
    let resolvedSku = addonSku;
    let sectionsHtml = null;
    // Captured from whichever /cart/add.js response actually put the addon in
    // the cart — returned to callers so they can track the add without a
    // separate cart refetch.
    let addedItem = null;

    // Build addon-specific link properties
    const linkProps = addonType === 'applecare'
      ? { _aplcr_main_product_sku: mainProductSku, _aplcr_main_product_handle: mainProductHandle }
      : { _sw_main_product_sku: mainProductSku, _sw_main_product_handle: mainProductHandle };

    // If addon handle unknown, add addon first to discover it, then clean up on error
    if (!resolvedHandle) {
      const prelimAdd = await fetch(routes.cart_add_url, {
        ...getFetchConfig('POST', {
          items: [{ id: variantId, quantity, properties: { _uuid: currentUuid, ...linkProps } }]
        })
      });
      if (!prelimAdd.ok) {
        const err = await prelimAdd.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to add add-on');
      }
      const prelimResult = await prelimAdd.json();
      const prelimItem = prelimResult.items?.[0];
      if (prelimItem) {
        resolvedHandle = prelimItem.handle;
        resolvedSku = prelimItem.sku;
        addedItem = prelimItem;
      }
    }

    if (!resolvedHandle) throw new Error('Could not determine add-on handle');

    const chainedUuid = currentUuid + '-' + resolvedHandle;

    // Update main product with back-link properties and chained UUID
    const mainProps = { ...mainItem.properties, _uuid: chainedUuid };
    if (addonType === 'applecare') {
      mainProps._applecare_sku = resolvedSku;
      mainProps._applecare_handle = resolvedHandle;
    } else {
      mainProps._secWarranty_sku = resolvedSku;
      mainProps._secWarranty_handle = resolvedHandle;
    }

    const changeResponse = await fetch(routes.cart_change_url, {
      ...getFetchConfig('POST', {
        id: mainItem.key,
        quantity: mainItem.quantity,
        properties: mainProps
      })
    });
    if (!changeResponse.ok) {
      // If we did a preliminary add, roll it back
      if (!addonHandle) {
        const rollbackCart = await this.getCartData(true);
        const orphan = rollbackCart.items.find(item =>
          item.variant_id === parseInt(variantId) &&
          (addonType === 'applecare'
            ? item.properties?._aplcr_main_product_sku === mainProductSku
            : item.properties?._sw_main_product_sku === mainProductSku)
        );
        if (orphan) {
          await fetch(routes.cart_change_url, {
            ...getFetchConfig('POST', { id: orphan.key, quantity: 0 })
          });
        }
      }
      throw new Error('Failed to update main product properties');
    }
    const changeResult = await changeResponse.json();

    // Cross-sync sibling addon's UUID (e.g. SW when adding AC+, or vice versa)
    await this._crossSyncAddonUuid(changeResult.items, mainProductSku, addonType, chainedUuid);

    if (addonHandle) {
      // Normal path: addon handle was known upfront, add the addon now.
      // Piggyback a section-render request on this POST when the caller
      // asked for it — saves a separate cart-page re-render round trip.
      const addBody = {
        items: [{ id: variantId, quantity, properties: { _uuid: chainedUuid, ...linkProps } }]
      };
      if (sections && sections.length > 0) {
        addBody.sections = sections.join(',');
        addBody.sections_url = routes.cart_url;
      }
      const addResponse = await fetch(routes.cart_add_url, {
        ...getFetchConfig('POST', addBody)
      });
      if (!addResponse.ok) {
        const err = await addResponse.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to add add-on');
      }
      // Always parse — tracking needs the added item, and the response is
      // small even without sections.
      const addResult = await addResponse.json().catch(() => null);
      if (sections && sections.length > 0) {
        sectionsHtml = addResult?.sections || null;
      }
      addedItem = addResult?.items?.[0] || addedItem;
    } else {
      // Fallback: addon was already added above with un-chained UUID — update it
      const freshCart = await this.getCartData(true);
      const addonInCart = freshCart.items.find(item =>
        item.variant_id === parseInt(variantId) &&
        (addonType === 'applecare'
          ? item.properties?._aplcr_main_product_sku === mainProductSku
          : item.properties?._sw_main_product_sku === mainProductSku) &&
        item.properties?._uuid !== chainedUuid
      );
      if (addonInCart) {
        const syncResp = await fetch(routes.cart_change_url, {
          ...getFetchConfig('POST', {
            id: addonInCart.key,
            quantity: addonInCart.quantity,
            properties: { ...addonInCart.properties, _uuid: chainedUuid }
          })
        });
        if (!syncResp.ok) console.warn('Failed to sync add-on UUID to chained value');
      }
    }

    return { sectionsHtml, addedItem };
  }

  /**
   * Remove back-link properties from the main product after an addon is removed.
   * Restores the UUID by stripping the addon handle suffix and cross-syncs
   * any remaining sibling addon.
   *
   * @param {Object} mainItem - Main product cart item (pre-removal snapshot)
   * @param {string} addonType - 'applecare' | 'secondwarranty'
   * @param {string} mainProductSku - SKU of the main product
   */
  async _removeBackLinkProperties(mainItem, addonType, mainProductSku) {
    const updatedProperties = { ...mainItem.properties };

    // Remove back-link properties and strip addon handle from UUID
    const handleKey = addonType === 'applecare' ? '_applecare_handle' : '_secWarranty_handle';
    const skuKey = addonType === 'applecare' ? '_applecare_sku' : '_secWarranty_sku';
    const handle = updatedProperties[handleKey];
    delete updatedProperties[skuKey];
    delete updatedProperties[handleKey];

    if (updatedProperties._uuid && handle) {
      const suffix = '-' + handle;
      if (updatedProperties._uuid.endsWith(suffix)) {
        updatedProperties._uuid = updatedProperties._uuid.slice(0, -suffix.length);
      } else if (updatedProperties._uuid.includes(suffix)) {
        updatedProperties._uuid = updatedProperties._uuid.replace(suffix, '');
      }
    }

    const changeResponse = await fetch(routes.cart_change_url, {
      ...getFetchConfig('POST', {
        id: mainItem.key,
        quantity: mainItem.quantity,
        properties: updatedProperties
      })
    });
    if (!changeResponse.ok) {
      console.warn('Failed to clean up main product back-link properties');
      return;
    }
    const changeResult = await changeResponse.json();

    // Cross-sync remaining sibling addon to the restored UUID
    await this._crossSyncAddonUuid(changeResult.items, mainProductSku, addonType, updatedProperties._uuid);
  }

  /**
   * Update a sibling addon's UUID to match the target UUID.
   * When AC+ is added/removed, SW (if present) needs its UUID updated, and vice versa.
   *
   * @param {Array} cartItems - Current cart items (from a /cart/change.js response)
   * @param {string} mainProductSku - SKU of the main product
   * @param {string} addonType - The addon type that was just added/removed
   * @param {string} targetUuid - The UUID all linked items should have
   */
  async _crossSyncAddonUuid(cartItems, mainProductSku, addonType, targetUuid) {
    if (!cartItems) return;
    const crossType = addonType === 'applecare' ? '_sw_main_product_sku' : '_aplcr_main_product_sku';
    const sibling = cartItems.find(item =>
      item.properties?.[crossType] === mainProductSku &&
      item.properties?._uuid !== targetUuid
    );
    if (sibling) {
      const resp = await fetch(routes.cart_change_url, {
        ...getFetchConfig('POST', {
          id: sibling.key,
          quantity: sibling.quantity,
          properties: { ...sibling.properties, _uuid: targetUuid }
        })
      });
      if (!resp.ok) console.warn('Failed to cross-sync sibling addon UUID');
    }
  }

  /**
   * Setup click handlers for addon buttons
   */
  setupAddonToggles() {
    this.querySelectorAll('.epl-addon-option .epl-addon-btn').forEach(btn => {
      // Picker-managed triggers only open the modal; cart-epl.js must not toggle.
      if (btn.dataset.addonPickerTrigger === 'true') return;

      // Remove existing listener if any (prevent duplicates)
      btn.removeEventListener('click', btn._handleClick);

      btn._handleClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const option = btn.closest('.epl-addon-option');
        if (!option) return;
        try {
          await this.handleAddonToggle(option);
        } finally {
          // Always re-render the cart items from server HTML after a toggle.
          // The optimistic UI inside handleAddonToggle gives instant feedback,
          // but every add-on property change rotates the main product's
          // line-item key and _uuid, leaving the hand-synced cached DOM keys
          // stale. A full server re-render rebuilds keys/uuids/option state
          // authoritatively, eliminating the stale-key class (e.g. the
          // "remove no-ops on the first click after toggling AC+" bug).
          //
          // Run the re-render THROUGH the operation lock. If the user queued an
          // op mid-toggle (e.g. clicked "Fjern" while AC+ was loading), it was
          // enqueued at click time — before this finally — so the lock runs it
          // FIRST (on the still-attached DOM), then this re-render reconciles.
          // Re-rendering outside the lock raced that queued removal and made it
          // intermittently no-op. Guard it: the mutation already committed
          // server-side, so a failed re-render must not become an unhandled
          // rejection — the next successful render reconciles the DOM.
          try {
            await this.executeWithLock(async () => { await this.updateCart(); });
          } catch (e) {
            console.error('Cart re-render after addon toggle failed:', e);
          }
        }
      };
      btn.addEventListener('click', btn._handleClick);
    });

    this.setupAddonPickerBridge();
  }

  /**
   * Listen for addon-picker:chosen events and route them through handleAddonToggle
   * by updating the matching .epl-addon-option element with the chosen variant
   * before triggering the add flow. Ensures the full optimistic UI pipeline runs.
   */
  setupAddonPickerBridge() {
    if (this._addonPickerBridgeBound) return;
    this._addonPickerBridgeBound = true;

    document.addEventListener('addon-picker:chosen', async (event) => {
      const detail = event.detail || {};
      const mainItemKey = detail.mainItemKey;
      if (!mainItemKey) return;

      // Bail if the triggering button lives in another cart surface (e.g.
      // the cart drawer). Without this check, picking in the drawer would
      // also run cart-epl.js's path because both surfaces render a row
      // with the same main-item-key on the cart page.
      // Require a contained trigger: if it's missing or detached (e.g. the
      // owning row was re-rendered between open and choose), bail — we'd
      // otherwise risk double-dispatch across both bridges.
      if (!detail.triggerElement || !this.contains(detail.triggerElement)) return;

      const option = this.querySelector(
        `.epl-addon-option[data-addon-type="secondwarranty"][data-main-item-key="${mainItemKey}"]`
      );
      if (!option) return;

      // Hard-bundled SW is mandatory: a pick means "swap type", never a
      // fresh add. Route to the swap flow and skip the toggle pipeline.
      if (option.dataset.swHardBundle === 'true') {
        await this.handleAddonSwap(option, detail);
        return;
      }

      option.dataset.variantId = detail.variantId || '';
      option.dataset.addonSku = detail.productSku || '';
      option.dataset.addonHandle = detail.productHandle || '';
      if (detail.productPrice) option.dataset.addonPrice = detail.productPrice;

      // The picker trigger button inside this option has ac-modal-trigger
      // attrs. Optimistic UI flips its text to "Fjern" but it still opens the
      // modal on click — the "stuck on Fjern" state. Request section re-render
      // in the same POST as the add so the server-rendered Fjern button (which
      // does NOT have modal-trigger attrs) replaces it without a second
      // network round trip.
      await this.handleAddonToggle(option, {
        sections: ['main-cart-items-epl', 'main-cart-footer-epl', 'cart-icon-bubble']
      });
    });
  }

  /**
   * Handle addon toggle (add/remove from cart) with optimistic UI updates
   * Updates UI immediately, syncs with server in background, no loading overlay
   * Uses operation lock to prevent race conditions from rapid clicks
   */
  async handleAddonToggle(option, { sections = null } = {}) {
    // Check if unavailable
    if (option.dataset.unavailable === 'true') {
      return;
    }

    // Prevent rapid clicks on same element while operation in progress
    if (option.dataset.operationPending === 'true') {
      return;
    }

    // Mark as pending immediately (before lock)
    option.dataset.operationPending = 'true';
    this._setCheckoutPending(true);

    try {
      await this.executeWithLock(async () => {
        // Re-read state inside lock as it may have changed while queued
        const isSelected = option.classList.contains('epl-addon-option--selected');
        const variantId = option.dataset.variantId;
        const itemKey = option.dataset.itemKey;
        const addonType = option.dataset.addonType;
        const mainProductSku = option.dataset.mainProductSku;
        const mainProductHandle = option.dataset.mainProductHandle;
        const mainProductUuid = option.dataset.mainProductUuid;
        const mainItemKey = option.dataset.mainItemKey;
        const addonPrice = parseInt(option.dataset.addonPrice) || 0;

        // Get fresh cart data (may have been refreshed for queued operations)
        const cartData = await this.getCartData();
        const mainItem = cartData.items.find(i => i.key === mainItemKey);
        const quantity = mainItem?.quantity || 1;
        const priceDelta = addonPrice * quantity * (isSelected ? -1 : 1);
        const itemCountDelta = isSelected ? -1 : 1;

        // 1. Optimistic UI update - toggle visual state immediately
        this.toggleAddonVisualState(option, !isSelected, quantity);

        // 2. Update totals optimistically (before server response)
        const newTotal = cartData.total_price + priceDelta;
        const newItemCount = cartData.item_count + itemCountDelta;
        this.updateTotalsOptimistic(newTotal, newItemCount);

        // 3. API call (add or remove) - no overlay needed
        try {
          let sectionsHtml = null;
          if (isSelected && itemKey) {
            // Remove addon from cart
            await this.removeAddonFromCart(option);
          } else if (!isSelected) {
            // Add addon to cart; piggybacks a section-render request when
            // the caller asked for it (e.g. the picker bridge).
            const result = await this.addAddonToCart(option, quantity, { sections });
            sectionsHtml = result?.sectionsHtml || null;
          }

          // 4. Fetch updated cart data and verify
          const newCartData = await this.getCartData(true);
          this.verifyAndUpdateAddon(option, newCartData);

          // 5. If we got sections back from the add POST, apply them in one
          //    pass. Otherwise fall back to piecemeal surgical updates.
          if (sectionsHtml) {
            await this._applyCartSections(sectionsHtml, newCartData);
          } else {
            await this.updateCartFooterOnly(newCartData);
            this.updateSummaryColumn(newCartData);
            this.updateCartIconBubble(newCartData.item_count);
          }

          // 6. Trigger cart:updated event
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: newCartData }));

        } catch (error) {
          console.error('Error toggling addon:', error);
          // Rollback optimistic UI on error
          this.toggleAddonVisualState(option, isSelected, quantity);
          this.showError('Kunne ikke oppdatere tilbehør. Prøv igjen.');
          // Full re-render as fallback to restore correct state
          await this.updateCart();
        }
      });
    } finally {
      // Clear pending flag when operation completes (success or failure)
      option.dataset.operationPending = 'false';
      this._setCheckoutPending(false);
    }
  }

  /**
   * Swap the Second Warranty line of a hard-bundled main product to another
   * picker entry. The addon is mandatory, so it is replaced, never removed:
   * update the main line's back-links (_secWarranty_sku/_handle) and _uuid
   * chain, remove the old SW line, then add the chosen variant with the same
   * link properties. The final add piggybacks a section render so the server
   * HTML replaces the optimistic state in one pass.
   * @param {HTMLElement} option - The .epl-addon-option row (hard-bundle)
   * @param {Object} detail - addon-picker:chosen event detail
   */
  async handleAddonSwap(option, detail) {
    if (option.dataset.operationPending === 'true') return;
    option.dataset.operationPending = 'true';
    this._setCheckoutPending(true);

    try {
      await this.executeWithLock(async () => {
        const mainItemKey = option.dataset.mainItemKey;
        const mainProductSku = option.dataset.mainProductSku;
        const mainProductUuid = option.dataset.mainProductUuid;

        const cartData = await this.getCartData(true);
        const mainItem = this._findMainProduct(cartData.items, { mainItemKey, mainProductSku, mainProductUuid });
        if (!mainItem) throw new Error('Main product not found in cart');

        const mainUuid = mainItem.properties?._uuid || mainProductUuid;
        const swLine = cartData.items.find(item =>
          item.properties?._sw_main_product_sku === mainItem.sku &&
          item.properties?._uuid === mainUuid
        );
        if (!swLine) throw new Error('Second Warranty line not found in cart');
        if (String(swLine.variant_id) === String(detail.variantId)) return;

        // Optimistic totals: item count is unchanged, only the price moves.
        const oldPrice = swLine.final_price || swLine.price || 0;
        const newPrice = parseInt(detail.productPrice, 10) || 0;
        const priceDelta = (newPrice - oldPrice) * swLine.quantity;
        this.updateTotalsOptimistic(cartData.total_price + priceDelta, cartData.item_count);

        try {
          const oldHandle = mainItem.properties?._secWarranty_handle || swLine.handle;
          const newHandle = detail.productHandle;
          // Replace the SW segment inside the uuid chain; an AC+ suffix, if
          // any, keeps its position.
          let newUuid = mainUuid;
          if (oldHandle && newHandle && mainUuid && mainUuid.indexOf('-' + oldHandle) !== -1) {
            newUuid = mainUuid.replace('-' + oldHandle, '-' + newHandle);
          }

          // 1. Main line: new back-links + uuid
          const mainProps = {
            ...mainItem.properties,
            _secWarranty_sku: detail.productSku,
            _secWarranty_handle: newHandle,
            _uuid: newUuid
          };
          const changeResponse = await fetch(routes.cart_change_url, {
            ...getFetchConfig('POST', { id: mainItem.key, quantity: mainItem.quantity, properties: mainProps })
          });
          if (!changeResponse.ok) throw new Error('Failed to update main product properties');
          const changeResult = await changeResponse.json();

          // 2. Keep a sibling AC+ line on the same uuid chain
          await this._crossSyncAddonUuid(changeResult.items, mainItem.sku, 'secondwarranty', newUuid);

          // 3. Old SW line out
          const removeResponse = await fetch(routes.cart_change_url, {
            ...getFetchConfig('POST', { id: swLine.key, quantity: 0 })
          });
          if (!removeResponse.ok) throw new Error('Failed to remove old Second Warranty line');

          // 4. Chosen variant in, same link properties, piggybacked sections
          const addBody = {
            items: [{
              id: detail.variantId,
              quantity: swLine.quantity,
              properties: { ...swLine.properties, _uuid: newUuid }
            }],
            sections: ['main-cart-items-epl', 'main-cart-footer-epl', 'cart-icon-bubble'].join(','),
            sections_url: routes.cart_url
          };
          const addResponse = await fetch(routes.cart_add_url, {
            ...getFetchConfig('POST', addBody)
          });
          if (!addResponse.ok) {
            const err = await addResponse.json().catch(() => ({}));
            throw new Error(err.message || 'Failed to add Second Warranty');
          }
          const addResult = await addResponse.json().catch(() => null);

          const newCartData = await this.getCartData(true);
          const sectionsHtml = addResult?.sections || null;
          if (sectionsHtml) {
            await this._applyCartSections(sectionsHtml, newCartData);
          } else {
            await this.updateCart();
          }
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: newCartData }));
        } catch (error) {
          console.error('Error swapping addon:', error);
          this.showError('Kunne ikke endre type. Prøv igjen.');
          // Full re-render restores authoritative state after a partial swap.
          await this.updateCart();
        }
      });
    } finally {
      option.dataset.operationPending = 'false';
      this._setCheckoutPending(false);
    }
  }

  /**
   * Toggle add-on visual state without server call
   * @param {HTMLElement} option - The .epl-addon-option element
   * @param {boolean} selected - Whether the add-on should appear selected
   * @param {number} quantity - Quantity of the add-on (matches main product)
   */
  toggleAddonVisualState(option, selected, quantity = 1) {
    option.classList.toggle('epl-addon-option--selected', selected);

    const btn = option.querySelector('.epl-addon-btn');
    if (btn) {
      btn.classList.toggle('epl-addon-btn--remove', selected);
      btn.textContent = selected ? 'Fjern' : 'Legg til';
    }

    // Update quantity display in price (e.g., "3 ×390 kr")
    const priceEl = option.querySelector('.epl-addon-price');
    if (priceEl) {
      const addonPrice = parseInt(option.dataset.addonPrice) || 0;
      const currency = this._cartDataCache?.currency || 'NOK';
      let qtySpan = priceEl.querySelector('.epl-addon-qty');

      if (selected && quantity > 1) {
        // Show quantity prefix
        if (!qtySpan) {
          qtySpan = document.createElement('span');
          qtySpan.className = 'epl-addon-qty';
          priceEl.insertBefore(qtySpan, priceEl.firstChild);
        }
        qtySpan.textContent = `${quantity} ×`;
      } else if (qtySpan) {
        // Remove quantity prefix when not selected or qty is 1
        qtySpan.remove();
      }
    }

    // Flash animation on the add-on card
    option.classList.add('epl-price-updated');
    setTimeout(() => option.classList.remove('epl-price-updated'), 300);
  }

  /**
   * Update totals optimistically (before server response)
   * @param {number} newTotal - New cart total in cents
   * @param {number} newItemCount - New item count
   */
  updateTotalsOptimistic(newTotal, newItemCount) {
    const currency = this._cartDataCache?.currency || 'NOK';

    // Update footer (mobile)
    const footerSubtotal = document.querySelector('.epl-cart-footer__subtotal-value');
    if (footerSubtotal) {
      writePrice(footerSubtotal, newTotal, currency);
      footerSubtotal.classList.add('epl-price-updated');
      setTimeout(() => footerSubtotal.classList.remove('epl-price-updated'), 300);
    }

    // Update summary column (desktop)
    const summarySubtotal = this.querySelector('.epl-summary-subtotal');
    const summaryTotal = this.querySelector('.epl-summary-total');
    if (summarySubtotal) {
      writePrice(summarySubtotal, newTotal, currency);
      summarySubtotal.classList.add('epl-price-updated');
      setTimeout(() => summarySubtotal.classList.remove('epl-price-updated'), 300);
    }
    if (summaryTotal) {
      writePrice(summaryTotal, newTotal, currency);
      summaryTotal.classList.add('epl-price-updated');
      setTimeout(() => summaryTotal.classList.remove('epl-price-updated'), 300);
    }

    // Update Klarna placement amount
    const klarnaPlacement = this.querySelector('#klarna-nmp-cart-summary');
    if (klarnaPlacement) {
      klarnaPlacement.setAttribute('data-purchase-amount', newTotal.toString());
      window.Klarna?.OnsiteMessaging?.refresh?.();
    }

    // Update the free-shipping barometer for the new total
    this.updateFreeShippingBar(newTotal, currency);

    // Update cart icon bubble optimistically
    this.updateCartIconBubble(newItemCount);
  }

  /**
   * Add add-on to cart (API call only, no UI updates)
   * @param {HTMLElement} option - The .epl-addon-option element
   * @param {number} quantity - Quantity to add (matches main product)
   */
  async addAddonToCart(option, quantity, { sections = null } = {}) {
    const variantId = option.dataset.variantId;
    const addonType = option.dataset.addonType;
    const mainProductSku = option.dataset.mainProductSku;
    const mainProductHandle = option.dataset.mainProductHandle;
    const mainItemKey = option.dataset.mainItemKey;
    const addonSku = option.dataset.addonSku;
    const addonHandle = option.dataset.addonHandle;
    const mainProductUuid = option.dataset.mainProductUuid;

    // Cache-first: handleAddonToggle already fetched the cart immediately
    // before calling us and the operation lock prevents concurrent mutation,
    // so forcing a refresh here is a wasted round trip.
    const cartData = await this.getCartData();
    const mainProductItem = this._findMainProduct(cartData.items, { mainItemKey, mainProductSku, mainProductUuid });
    if (!mainProductItem) throw new Error('Main product not found in cart');

    const currentUuid = mainProductItem.properties?._uuid || mainProductUuid || mainProductHandle;

    let sectionsHtml = null;
    let addedItem = null;

    if (addonType === 'applecare' || addonType === 'secondwarranty') {
      const result = await this._addBackLinkedAddon({
        variantId, quantity, addonType, mainProductSku, mainProductHandle,
        mainItem: mainProductItem, currentUuid, addonSku, addonHandle, sections
      });
      sectionsHtml = result?.sectionsHtml || null;
      addedItem = result?.addedItem || null;
    } else {
      // Vegglader: no back-link properties on main product
      const veggladerBody = {
        items: [{ id: variantId, quantity, properties: {
          _uuid: currentUuid,
          _vegglader_main_product_sku: mainProductSku,
          _vegglader_main_product_handle: mainProductHandle,
        }}]
      };
      if (sections && sections.length > 0) {
        veggladerBody.sections = sections.join(',');
        veggladerBody.sections_url = routes.cart_url;
      }
      const response = await fetch(routes.cart_add_url, {
        ...getFetchConfig('POST', veggladerBody)
      });
      if (!response.ok) throw new Error('Failed to add add-on');
      const addResult = await response.json().catch(() => null);
      if (sections && sections.length > 0) {
        sectionsHtml = addResult?.sections || null;
      }
      addedItem = addResult?.items?.[0] || null;
    }

    // Track add to cart using the added item from the add.js response — no
    // extra cart refetch needed.
    if (addedItem) {
      const currency = this._cartDataCache?.currency || 'NOK';
      const value = (addedItem.final_line_price || addedItem.line_price || 0) / 100;
      trackCartEvent('add_to_cart', {
        sku: addedItem.sku,
        variant_id: addedItem.variant_id,
        product_title: addedItem.product_title,
        quantity: addedItem.quantity,
        final_price: addedItem.final_price || addedItem.price
      }, currency, value);
    }

    return { sectionsHtml };
  }

  /**
   * Remove add-on from cart (API call only, no UI updates)
   * @param {HTMLElement} option - The .epl-addon-option element
   */
  async removeAddonFromCart(option) {
    const itemKey = option.dataset.itemKey;
    if (!itemKey) return;

    const addonType = option.dataset.addonType;
    const mainProductSku = option.dataset.mainProductSku;
    const mainItemKey = option.dataset.mainItemKey;
    const mainProductUuid = option.dataset.mainProductUuid;

    const cartData = await this.getCartData(true);
    const itemToRemove = cartData.items.find(item => item.key === itemKey);

    // Remove the add-on
    const response = await fetch(routes.cart_change_url, {
      ...getFetchConfig('POST', { id: itemKey, quantity: 0 })
    });
    if (!response.ok) throw new Error('Failed to remove add-on');

    // For AC+ and SW: clean up main product back-link properties
    if (addonType === 'applecare' || addonType === 'secondwarranty') {
      const mainProductItem = this._findMainProduct(cartData.items, { mainItemKey, mainProductSku, mainProductUuid });
      if (mainProductItem) {
        await this._removeBackLinkProperties(mainProductItem, addonType, mainProductSku);
      }
    }

    // Track removal
    if (itemToRemove) {
      const currency = cartData.currency || 'NOK';
      const value = (itemToRemove.final_line_price || itemToRemove.line_price || 0) / 100;
      trackCartEvent('remove_from_cart', {
        sku: itemToRemove.sku,
        variant_id: itemToRemove.variant_id,
        product_title: itemToRemove.product_title,
        quantity: itemToRemove.quantity,
        final_price: itemToRemove.final_price || itemToRemove.price
      }, currency, value);
    }
  }

  /**
   * Verify server response and update data-item-key attribute
   * @param {HTMLElement} option - The .epl-addon-option element
   * @param {Object} cartData - Cart data from server
   */
  verifyAndUpdateAddon(option, cartData) {
    const variantId = parseInt(option.dataset.variantId);
    const mainProductSku = option.dataset.mainProductSku;
    const addonType = option.dataset.addonType;
    const mainProductUuid = option.dataset.mainProductUuid;

    // UUID matching: use startsWith to handle chained UUIDs (e.g., "baseUuid-acHandle-swHandle")
    // One-directional: this matches add-on/sibling lines to THIS main using fresh
    // data (add-on uuid is equal to or a base prefix of the main's). Bidirectional
    // here would over-match a chained add-on against a shorter same-SKU instance.
    const uuidMatchFn = (itemUuid) => {
      if (!mainProductUuid) return true;
      return itemUuid === mainProductUuid || (itemUuid && itemUuid.startsWith(mainProductUuid));
    };

    // Find the add-on item in updated cart
    const addonItem = cartData.items.find(item => {
      if (item.variant_id !== variantId) return false;
      const props = item.properties || {};

      if (addonType === 'applecare') {
        return props._aplcr_main_product_sku === mainProductSku && uuidMatchFn(props._uuid);
      } else if (addonType === 'secondwarranty') {
        return props._sw_main_product_sku === mainProductSku && uuidMatchFn(props._uuid);
      } else if (addonType === 'vegglader') {
        return props._vegglader_main_product_sku === mainProductSku && uuidMatchFn(props._uuid);
      }
      return false;
    });

    // Find the main product in cart data (key changes when properties change)
    const mainItem = this._findMainProduct(cartData.items, { mainProductSku, mainProductUuid });

    // Update data-item-key attribute
    if (addonItem) {
      option.dataset.itemKey = addonItem.key;
      if (mainItem) {
        option.dataset.mainItemKey = mainItem.key;
        option.dataset.mainProductUuid = mainItem.properties?._uuid || mainProductUuid;
      }
    } else {
      option.dataset.itemKey = '';
    }

    // Re-sync cached line-item keys after the main product's key rotated.
    // The main product's key changes whenever its properties change (every add-on
    // toggle mutates _uuid + back-link props). These cached keys target cart
    // operations — the container key drives quantity changes (handleQuantityChange)
    // and add-on routing (itemHasAddOns); the remove button key drives removal
    // (CartRemoveButtonEpl.handleClick) — so a stale key makes the next op silently
    // no-op until a full re-render. Keep them all fresh here.
    const cartItem = option.closest('.epl-cart-item');
    if (cartItem && mainItem) {
      cartItem.dataset.itemKey = mainItem.key;

      const allAddonOptions = cartItem.querySelectorAll('.epl-addon-option');
      allAddonOptions.forEach(siblingOption => {
        if (siblingOption === option) return; // already updated above
        if (siblingOption.dataset.mainProductSku !== mainProductSku) return;

        siblingOption.dataset.mainItemKey = mainItem.key;
        siblingOption.dataset.mainProductUuid = mainItem.properties?._uuid || mainProductUuid;

        // A sibling add-on's own line key also rotates when _crossSyncAddonUuid
        // rewrites its _uuid to match the main. Re-resolve it so toggling the
        // sibling off later doesn't target a dead key.
        const sibType = siblingOption.dataset.addonType;
        const sibVariantId = parseInt(siblingOption.dataset.variantId);
        const sibSkuProp = sibType === 'applecare' ? '_aplcr_main_product_sku'
          : sibType === 'secondwarranty' ? '_sw_main_product_sku'
          : sibType === 'vegglader' ? '_vegglader_main_product_sku'
          : null;
        if (!sibSkuProp) return;
        const sibItem = cartData.items.find(item =>
          item.variant_id === sibVariantId &&
          item.properties?.[sibSkuProp] === mainProductSku &&
          uuidMatchFn(item.properties?._uuid)
        );
        if (sibItem) siblingOption.dataset.itemKey = sibItem.key;
      });
    }

    // Keep the remove button's cached key + addon-state flag in sync.
    // data-item-key must track the rotated main key (else removal silently
    // no-ops); data-typeofbutton must reflect whether add-ons remain so removing
    // the main product cascade-removes them.
    if (cartItem) {
      const removeButton = cartItem.querySelector('cart-remove-button-epl');
      if (removeButton) {
        if (mainItem) removeButton.dataset.itemKey = mainItem.key;
        const hasAddons = cartData.items.some(item => {
          const props = item.properties || {};
          if (!uuidMatchFn(props._uuid)) return false;
          return (
            props._aplcr_main_product_sku === mainProductSku ||
            props._sw_main_product_sku === mainProductSku ||
            props._vegglader_main_product_sku === mainProductSku ||
            props._gwp_main_product_sku === mainProductSku
          );
        });
        removeButton.dataset.typeofbutton = hasAddons ? 'main_product' : 'nonaplcr';
      }
    }
  }

  /**
   * Setup click handlers for upsell quick-add buttons
   */
  setupUpsellButtons() {
    this.querySelectorAll('.epl-add-upsell-btn, .epl-cart-drawer-atc-button').forEach(btn => {
      // Remove existing listener if any (prevent duplicates)
      btn.removeEventListener('click', btn._handleClick);

      btn._handleClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        await this.handleUpsellQuickAdd(btn);
      };
      btn.addEventListener('click', btn._handleClick);
    });
  }

  /**
   * Handle upsell quick-add button click
   */
  async handleUpsellQuickAdd(btn) {
    const variantId = btn.dataset.variantId;
    const originalText = btn.textContent;

    if (!variantId) {
      console.error('Variant ID not found for upsell');
      return;
    }

    // Show loading overlay
    this.showLoadingOverlay();

    // Disable button and show loading
    btn.disabled = true;
    btn.textContent = 'Legger til...';

    try {
      // First check if this variant already exists in the cart
      const currentCart = await fetch(`${routes.cart_url}.js`).then(r => r.json());
      const existingItem = currentCart.items.find(item => item.variant_id === parseInt(variantId));

      let response;
      if (existingItem) {
        // Item exists - update quantity using change endpoint (keeps position)
        response = await fetch(`${routes.cart_change_url}`, {
          ...getFetchConfig('POST', {
            id: existingItem.key,
            quantity: existingItem.quantity + 1
          })
        });
      } else {
        // New item - add to cart
        response = await fetch(`${routes.cart_add_url}`, {
          ...getFetchConfig('POST', {
            items: [{
              id: variantId,
              quantity: 1
            }]
          })
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add item');
      }

      // Get cart data for GA tracking
      const cartData = await fetch(`${routes.cart_url}.js`).then(r => r.json());
      const addedItem = cartData.items.find(item => item.variant_id === parseInt(variantId));

      // Track add to cart
      if (addedItem) {
        const currency = cartData.currency || window.themeJsData?.shopCurrency || 'NOK';
        const value = (addedItem.final_line_price || addedItem.line_price || 0) / 100;
        trackCartEvent('add_to_cart', {
          sku: addedItem.sku,
          variant_id: addedItem.variant_id,
          product_title: addedItem.product_title,
          quantity: 1,
          final_price: addedItem.final_price || addedItem.price
        }, currency, value);
      }

      // Show success
      btn.textContent = '✓ Lagt til';
      btn.classList.add('epl-add-upsell-btn--added', 'epl-cart-drawer-button--added');

      // Update cart display
      await this.updateCart();

      // Hide loading overlay
      this.hideLoadingOverlay();

      // Reset button after delay
      setTimeout(() => {
        btn.textContent = originalText;
        btn.classList.remove('epl-add-upsell-btn--added', 'epl-cart-drawer-button--added');
        btn.disabled = false;
      }, 2000);

    } catch (error) {
      console.error('Error adding upsell:', error);
      btn.textContent = 'Feil';

      // Hide loading overlay
      this.hideLoadingOverlay();

      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 2000);
    }
  }

  async handleQuantityChange(input) {
    const index = input.dataset.index;
    const quantity = parseInt(input.value) || 0;
    const cartItem = input.closest('.epl-cart-item');
    // Try both camelCase and kebab-case data attributes
    let itemKey = cartItem?.dataset.itemKey || cartItem?.getAttribute('data-item-key');

    if (quantity < 1) {
      input.value = 1;
      // Update minus button state
      const quantityInput = input.closest('quantity-input-epl');
      if (quantityInput && quantityInput.minusButton) {
        quantityInput.minusButton.disabled = true;
        quantityInput.minusButton.classList.add('disabled');
      }
      return;
    }

    if (!itemKey) {
      console.error('Item key not found');
      return;
    }

    // The cached key rotates after add-on property mutations; resolve the live
    // line (and self-heal the attribute) so the change targets the right item
    // instead of mis-routing through itemHasAddOns and silently no-opping.
    const resolved = this._resolveMainItem(cartItem, itemKey, await this.getCartData());
    if (resolved) itemKey = resolved.key;

    // Tiered Update Strategy:
    // Tier 1 (Optimistic): Simple quantity change with no add-ons - JS-only price update + footer fetch
    // Tier 2 (Optimistic with Add-ons): Quantity change with add-ons - cascading optimistic updates
    const hasAddOns = await this.itemHasAddOns(itemKey);

    if (!hasAddOns) {
      // Tier 1: Optimistic update for items without add-ons
      this.debouncedUpdateQuantityOptimistic(itemKey, quantity, cartItem);
    } else {
      // Tier 2: Optimistic update for items WITH add-ons (cascading updates)
      this.debouncedUpdateQuantityWithAddOns(itemKey, quantity, cartItem);
    }
  }

  /**
   * Debounced wrapper for optimistic quantity update WITH add-ons
   * Uses operation lock to prevent race conditions with concurrent operations
   */
  debouncedUpdateQuantityWithAddOns = debounce(async (itemKey, quantity, cartItem) => {
    // Mark quantity element as pending
    const quantityElement = cartItem?.querySelector('quantity-input-epl');
    if (quantityElement) {
      quantityElement.dataset.operationPending = 'true';
    }

    try {
      await this.executeWithLock(async () => {
        // Get fresh cart data inside lock (may have changed if operation was queued)
        const oldCartData = await this.getCartData();
        const oldItem = oldCartData.items.find(item => item.key === itemKey);

        // If item no longer exists (removed by another operation), skip
        if (!oldItem) {
          console.warn('Item no longer exists in cart, skipping quantity update with add-ons');
          return;
        }

        const oldQuantity = oldItem.quantity;

        await this.updateQuantityWithAddOnsOptimistic(itemKey, quantity, cartItem, oldQuantity, oldCartData);
      });
    } catch (error) {
      console.error('Error in optimistic update with add-ons:', error);
      // Fallback to full re-render on any error
      this.showLoadingOverlay();
      await this.updateCart();
      this.hideLoadingOverlay();
    } finally {
      // Clear pending state
      if (quantityElement) {
        quantityElement.dataset.operationPending = 'false';
      }
    }
  }, 500);

  /**
   * Check if a cart item has associated add-ons (AppleCare+, Second Warranty, etc.)
   * Uses cached cart data when available to minimize API calls
   * @param {string} itemKey - The cart item key
   * @returns {Promise<boolean>} True if item has add-ons
   */
  async itemHasAddOns(itemKey) {
    const cartData = await this.getCartData();
    const item = cartData.items.find(i => i.key === itemKey);
    if (!item) return false;

    const mainSku = item.sku;
    const mainUuid = item.properties?._uuid;

    // Check if any other items are linked to this item as add-ons
    return cartData.items.some(i => {
      if (i.key === itemKey) return false;
      const props = i.properties || {};
      const isLinked = (
        props._aplcr_main_product_sku === mainSku ||
        props._sw_main_product_sku === mainSku ||
        props._vegglader_main_product_sku === mainSku ||
        props._gwp_main_product_sku === mainSku
      );
      const uuidMatches = addonUuidMatchesMain(props._uuid, mainUuid);
      return isLinked && uuidMatches;
    });
  }

  async handleAddOnQuantityChange(input) {
    const quantity = parseInt(input.value) || 0;
    const itemKey = input.dataset.itemKey || input.getAttribute('data-item-key');
    const addonType = input.dataset.addonType || input.getAttribute('data-addon-type');
    const variantId = input.dataset.variantId || input.getAttribute('data-variant-id');
    const mainProductSku = input.dataset.mainProductSku || input.getAttribute('data-main-product-sku');
    const mainProductHandle = input.dataset.mainProductHandle || input.getAttribute('data-main-product-handle');
    const mainProductUuid = input.dataset.mainProductUuid || input.getAttribute('data-main-product-uuid');

    if (!addonType || !variantId || !mainProductSku) {
      console.error('Missing required data attributes for add-on quantity change');
      return;
    }

    // Show full viewport loading overlay
    this.showLoadingOverlay();

    try {
      // Use operation lock to prevent race conditions
      await this.executeWithLock(async () => {
        // Get fresh cart data inside lock
        const cartData = await this.getCartData(true);
        // Re-check if item exists with current key (may have changed)
        const currentItem = itemKey ? cartData.items.find(i => i.key === itemKey) : null;
        const currentItemKey = currentItem ? currentItem.key : null;

        if (quantity === 0) {
          // Remove add-on from cart
          if (currentItemKey) {
            await this.removeUpsellByKey(currentItemKey, addonType);
            this.invalidateCartCache();
            await this.updateCart();
          }
          // If no itemKey, item is not in cart, nothing to remove
        } else {
          // Add or update add-on quantity
          if (currentItemKey) {
            // Item exists in cart, update quantity
            const oldQuantity = currentItem.quantity;

            // Pass isAddOn=true to ensure full cart update for add-ons
            await this.updateQuantity(currentItemKey, quantity, oldQuantity, cartData.currency, true);
          } else {
            // Item doesn't exist, add it
            // Get add-on SKU/handle from the closest epl-addon-option element
            const addonOption = input.closest?.('.epl-addon-option') || input.closest?.('[data-addon-type]');
            const addonSku = addonOption?.dataset.addonSku || input.dataset.addonSku || '';
            const addonHandle = addonOption?.dataset.addonHandle || input.dataset.addonHandle || '';
            const mainItemKeyForAdd = addonOption?.dataset.mainItemKey || input.dataset.mainItemKey || '';

            const buttonData = {
              dataset: {
                variantId: variantId,
                variantid: variantId,
                skuparent: mainProductSku,
                mainProductSku: mainProductSku,
                handleparent: mainProductHandle,
                mainProductHandle: mainProductHandle,
                uuid: mainProductUuid,
                mainProductUuid: mainProductUuid,
                addonSku: addonSku,
                addonHandle: addonHandle,
                mainItemKey: mainItemKeyForAdd,
                keyparent: mainItemKeyForAdd
              },
              getAttribute: (attr) => {
                const camelCase = attr.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                return buttonData.dataset[camelCase] || null;
              }
            };

            // Add the add-on with the specified quantity
            await this.addUpsell(buttonData, addonType);

            // Get the newly added item key
            const freshCartData = await this.getCartData(true);
            const newItem = freshCartData.items.find(item => {
              if (item.variant_id !== parseInt(variantId)) return false;
              const itemUuid = item.properties?._uuid;
              const uuidOk = !mainProductUuid || itemUuid === mainProductUuid || (itemUuid && itemUuid.startsWith(mainProductUuid));
              return uuidOk &&
                (addonType === 'applecare' ? item.properties?._aplcr_main_product_sku === mainProductSku :
                 addonType === 'secondwarranty' ? item.properties?._sw_main_product_sku === mainProductSku :
                 addonType === 'vegglader' ? item.properties?._vegglader_main_product_sku === mainProductSku :
                 false);
            });

            if (newItem) {
              // If quantity > 1, update it after adding
              if (quantity > 1) {
                await this.updateQuantity(newItem.key, quantity, newItem.quantity, freshCartData.currency, true);
              } else {
                await this.updateCart();
              }
            } else {
              console.warn('Newly added item not found in cart, updating cart anyway');
              await this.updateCart();
            }
          }
        }
      });
    } catch (error) {
      console.error('Error handling add-on quantity change:', error);
      this.showError('Kunne ikke oppdatere tilbehør. Prøv igjen.');
      // Revert input value on error
      const cartData = await this.getCartData(true);
      if (itemKey) {
        const item = cartData.items.find(i => i.key === itemKey);
        if (item) {
          input.value = item.quantity;
        }
      } else {
        input.value = 0;
      }
    } finally {
      // Hide loading overlay
      this.hideLoadingOverlay();
    }
  }

  async removeUpsellByKey(itemKey, addonType) {
    const cartData = await this.getCartData(true);
    const itemToRemove = cartData.items.find(item => item.key === itemKey);

    if (!itemToRemove) {
      return;
    }

    // Remove the add-on
    const response = await fetch(routes.cart_change_url, {
      ...getFetchConfig('POST', { id: itemKey, quantity: 0 })
    });
    if (!response.ok) throw new Error('Failed to remove upsell');

    // For AC+ and SW: clean up main product back-link properties
    if (addonType === 'applecare' || addonType === 'secondwarranty') {
      const mainProductSku = addonType === 'applecare'
        ? itemToRemove.properties?._aplcr_main_product_sku
        : itemToRemove.properties?._sw_main_product_sku;
      const mainProductUuid = itemToRemove.properties?._uuid;

      if (mainProductSku) {
        const mainProductItem = this._findMainProduct(cartData.items, { mainProductSku, mainProductUuid });
        if (mainProductItem) {
          await this._removeBackLinkProperties(mainProductItem, addonType, mainProductSku);
        }
      }
    }
  }

  startPriceLoading(cartItem) {
    // Add loading state to price container (hides discount prices too)
    const priceContainer = cartItem.querySelector('.epl-cart-item__price-container');
    if (priceContainer) {
      priceContainer.classList.add('epl-price-updating');
      this.addSpinnerToElement(priceContainer);
    }

    // Add spinner and loading state to net-monthly-pricing (find the wrapper div)
    const nmpElement = cartItem.querySelector('.net-monthly-pricing');
    if (nmpElement) {
      nmpElement.classList.add('epl-price-updating');
      this.addSpinnerToElement(nmpElement);
    }

    // Add spinner to AC+ and SW prices in the upsells section
    const upsellsSection = cartItem.querySelector('.epl-cart-item__upsells');
    if (upsellsSection) {
      const acPriceElements = upsellsSection.querySelectorAll('.epl-upsell__price');
      acPriceElements.forEach(priceEl => {
        priceEl.classList.add('epl-price-updating');
        this.addSpinnerToElement(priceEl);
      });
    }
  }

  stopPriceLoading(cartItem) {
    // Remove loading state from price container
    const priceContainer = cartItem.querySelector('.epl-cart-item__price-container');
    if (priceContainer) {
      priceContainer.classList.remove('epl-price-updating');
      this.removeSpinnerFromElement(priceContainer);
    }

    // Remove spinner and loading state from net-monthly-pricing
    const nmpElement = cartItem.querySelector('.net-monthly-pricing');
    if (nmpElement) {
      nmpElement.classList.remove('epl-price-updating');
      this.removeSpinnerFromElement(nmpElement);
    }

    // Remove spinner from AC+ and SW prices in the upsells section
    const upsellsSection = cartItem.querySelector('.epl-cart-item__upsells');
    if (upsellsSection) {
      const acPriceElements = upsellsSection.querySelectorAll('.epl-upsell__price');
      acPriceElements.forEach(priceEl => {
        priceEl.classList.remove('epl-price-updating');
        this.removeSpinnerFromElement(priceEl);
      });
    }
  }

  /**
   * Add a spinner element inside an element (replaces content visually)
   * @param {HTMLElement} element - The element to add spinner to
   */
  addSpinnerToElement(element) {
    // Check if spinner already exists
    if (element.querySelector('.epl-price-spinner')) {
      return;
    }
    const spinner = document.createElement('span');
    spinner.className = 'epl-price-spinner';
    spinner.setAttribute('aria-hidden', 'true');
    element.appendChild(spinner);
  }

  /**
   * Remove spinner element from inside an element
   * @param {HTMLElement} element - The element to remove spinner from
   */
  removeSpinnerFromElement(element) {
    const spinner = element.querySelector('.epl-price-spinner');
    if (spinner) {
      spinner.remove();
    }
  }

  async updateQuantityDebounced(itemKey, quantity, cartItem) {
    // Show loading overlay now that debounce has completed
    this.showLoadingOverlay();

    try {
      // Get old quantity for GA tracking
      const oldCartData = await fetch(`${routes.cart_url}.js`).then(r => r.json());
      const oldItem = oldCartData.items.find(item => item.key === itemKey);
      const oldQuantity = oldItem ? oldItem.quantity : 0;

      await this.updateQuantity(itemKey, quantity, oldQuantity, oldCartData.currency);
    } catch (error) {
      console.error('Error updating quantity:', error);
      this.showError('Kunne ikke oppdatere mengde. Prøv igjen.');
      // Revert optimistic update by refreshing cart
      await this.updateCart();
    } finally {
      // Hide loading overlay
      this.hideLoadingOverlay();
    }
  }

  // ============================================================================
  // Optimistic Update Methods (Phase 2)
  // ============================================================================

  /**
   * Debounced wrapper for optimistic quantity update
   * Called after debounce delay for items without add-ons
   * Uses operation lock to prevent race conditions with concurrent operations
   */
  async updateQuantityOptimisticDebounced(itemKey, quantity, cartItem) {
    // Mark quantity element as pending
    const quantityElement = cartItem?.querySelector('quantity-input-epl');
    if (quantityElement) {
      quantityElement.dataset.operationPending = 'true';
    }

    try {
      await this.executeWithLock(async () => {
        // Get fresh cart data inside lock (may have changed if operation was queued)
        const oldCartData = await this.getCartData();
        const oldItem = oldCartData.items.find(item => item.key === itemKey);

        // If item no longer exists (removed by another operation), skip
        if (!oldItem) {
          console.warn('Item no longer exists in cart, skipping quantity update');
          return;
        }

        const oldQuantity = oldItem.quantity;

        await this.updateQuantityOptimistic(itemKey, quantity, cartItem, oldQuantity, oldCartData.currency);
      });
    } catch (error) {
      console.error('Error in optimistic update:', error);
      // Fallback to full re-render on any error
      this.showLoadingOverlay();
      await this.updateCart();
      this.hideLoadingOverlay();
    } finally {
      // Clear pending state
      if (quantityElement) {
        quantityElement.dataset.operationPending = 'false';
      }
    }
  }

  /**
   * Optimistic quantity update for items WITHOUT add-ons (Tier 1)
   * Updates price display immediately, then syncs with server
   * Only footer is fetched from server - cart items are NOT re-rendered
   *
   * @param {string} itemKey - The cart item key
   * @param {number} newQuantity - The new quantity
   * @param {HTMLElement} cartItem - The cart item DOM element
   * @param {number} oldQuantity - The previous quantity (for GA tracking)
   * @param {string} currency - Currency code
   */
  async updateQuantityOptimistic(itemKey, newQuantity, cartItem, oldQuantity, currency) {
    // 1. Immediate optimistic UI update (no waiting for server)
    this.updateItemPriceDisplay(cartItem, newQuantity);

    // 2. POST to cart API
    const response = await fetch(routes.cart_update_url, {
      ...getFetchConfig('POST', { updates: { [itemKey]: newQuantity } })
    });

    if (!response.ok) {
      // Server rejected - fallback to full re-render
      console.warn('Cart update failed, falling back to full re-render');
      this.showLoadingOverlay();
      await this.updateCart();
      this.hideLoadingOverlay();
      return;
    }

    // 3. Update cache with server response
    const cartData = await response.json();
    this.updateCartCache(cartData);

    // 4. Verify optimistic price matches server (handle edge cases like discounts)
    const serverItem = cartData.items.find(i => i.key === itemKey);
    if (serverItem) {
      this.verifyItemPrice(cartItem, serverItem, cartData.currency || currency || 'NOK');
    }

    // 5. Track GA event for quantity change
    if (oldQuantity !== newQuantity && serverItem) {
      const eventType = newQuantity > oldQuantity ? 'add_to_cart' : 'remove_from_cart';
      const quantityDiff = Math.abs(newQuantity - oldQuantity);
      const itemPrice = (serverItem.final_price || serverItem.price || 0) / 100;
      const value = itemPrice * quantityDiff;
      const trackCurrency = cartData.currency || currency || 'NOK';

      trackCartEvent(eventType, {
        sku: serverItem.sku,
        variant_id: serverItem.variant_id,
        product_title: serverItem.product_title,
        quantity: quantityDiff,
        final_price: serverItem.final_price || serverItem.price
      }, trackCurrency, value);
    }

    // 6. Update footer (mobile) + summary column (desktop) + cart icon bubble
    await this.updateCartFooterOnly(cartData);
    this.updateSummaryColumn(cartData);
    this.updateCartIconBubble(cartData.item_count);

    // 7. Trigger cart:updated event for other scripts
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));
  }

  /**
   * Optimistic quantity update for items WITH add-ons (Tier 2)
   * Updates main item + all linked add-ons optimistically, then syncs with server
   *
   * @param {string} itemKey - The main cart item key
   * @param {number} newQuantity - The new quantity
   * @param {HTMLElement} cartItem - The main cart item DOM element
   * @param {number} oldQuantity - The previous quantity
   * @param {Object} oldCartData - Cart data before update
   */
  async updateQuantityWithAddOnsOptimistic(itemKey, newQuantity, cartItem, oldQuantity, oldCartData) {
    const currency = oldCartData.currency || 'NOK';
    const mainItem = oldCartData.items.find(i => i.key === itemKey);
    if (!mainItem) return;

    const mainSku = mainItem.sku;
    const mainUuid = mainItem.properties?._uuid;

    // Find all linked add-ons
    const addOnItems = oldCartData.items.filter(item => {
      if (item.key === itemKey) return false;
      const props = item.properties || {};
      const isLinked = (
        props._aplcr_main_product_sku === mainSku ||
        props._sw_main_product_sku === mainSku ||
        props._vegglader_main_product_sku === mainSku ||
        props._gwp_main_product_sku === mainSku
      );
      const uuidMatches = addonUuidMatchesMain(props._uuid, mainUuid);
      return isLinked && uuidMatches;
    });

    // 1. Optimistic UI update for main item
    this.updateItemPriceDisplay(cartItem, newQuantity);

    // 2. Optimistic UI update for all add-ons (quantity display + totals calculation)
    let totalPriceDelta = 0;
    const mainUnitPrice = parseInt(cartItem.dataset.unitPrice) || (mainItem.final_price || mainItem.price || 0);
    totalPriceDelta += mainUnitPrice * (newQuantity - oldQuantity);

    addOnItems.forEach(addOn => {
      const addOnUnitPrice = addOn.final_price || addOn.price || 0;
      totalPriceDelta += addOnUnitPrice * (newQuantity - oldQuantity);

      // Update add-on quantity display in the addon options section
      this.updateAddonQuantityDisplay(addOn, newQuantity, currency);
    });

    // 3. Update totals optimistically
    const newTotal = oldCartData.total_price + totalPriceDelta;
    this.updateTotalsOptimistic(newTotal, oldCartData.item_count);

    // 4. Build updates object for API call (main item + all add-ons)
    const updates = { [itemKey]: newQuantity };
    addOnItems.forEach(addOn => {
      updates[addOn.key] = newQuantity;
    });

    // 5. POST to cart API
    const response = await fetch(routes.cart_update_url, {
      ...getFetchConfig('POST', { updates })
    });

    if (!response.ok) {
      // Server rejected - fallback to full re-render
      console.warn('Cart update with add-ons failed, falling back to full re-render');
      this.showLoadingOverlay();
      await this.updateCart();
      this.hideLoadingOverlay();
      return;
    }

    // 6. Update cache with server response
    const cartData = await response.json();
    this.updateCartCache(cartData);

    // 7. Verify and correct main item price
    const serverMainItem = cartData.items.find(i => i.key === itemKey);
    if (serverMainItem) {
      this.verifyItemPrice(cartItem, serverMainItem, currency);
    }

    // 8. Track GA event for quantity change
    if (oldQuantity !== newQuantity && serverMainItem) {
      const eventType = newQuantity > oldQuantity ? 'add_to_cart' : 'remove_from_cart';
      const quantityDiff = Math.abs(newQuantity - oldQuantity);
      const itemPrice = (serverMainItem.final_price || serverMainItem.price || 0) / 100;
      const value = itemPrice * quantityDiff;

      trackCartEvent(eventType, {
        sku: serverMainItem.sku,
        variant_id: serverMainItem.variant_id,
        product_title: serverMainItem.product_title,
        quantity: quantityDiff,
        final_price: serverMainItem.final_price || serverMainItem.price
      }, currency, value);
    }

    // 9. Update footer (mobile) + summary column (desktop) + cart icon bubble
    await this.updateCartFooterOnly(cartData);
    this.updateSummaryColumn(cartData);
    this.updateCartIconBubble(cartData.item_count);

    // 10. Trigger cart:updated event for other scripts
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));
  }

  /**
   * Update add-on quantity display in the addon options section
   * @param {Object} addOn - The add-on item from cart data
   * @param {number} newQuantity - The new quantity
   * @param {string} currency - Currency code
   */
  updateAddonQuantityDisplay(addOn, newQuantity, currency) {
    // Find the addon option element by variant ID and type
    const variantId = addOn.variant_id;
    const props = addOn.properties || {};

    // Determine addon type from properties
    let addonType = null;
    if (props._aplcr_main_product_sku) addonType = 'applecare';
    else if (props._sw_main_product_sku) addonType = 'secondwarranty';
    else if (props._vegglader_main_product_sku) addonType = 'vegglader';

    if (!addonType) return;

    // Find the addon option element
    const addonOption = this.querySelector(
      `.epl-addon-option[data-addon-type="${addonType}"][data-variant-id="${variantId}"]`
    );
    if (!addonOption) return;

    // Update quantity display in price
    const priceEl = addonOption.querySelector('.epl-addon-price');
    if (priceEl) {
      let qtySpan = priceEl.querySelector('.epl-addon-qty');

      if (newQuantity > 1) {
        if (!qtySpan) {
          qtySpan = document.createElement('span');
          qtySpan.className = 'epl-addon-qty';
          priceEl.insertBefore(qtySpan, priceEl.firstChild);
        }
        qtySpan.textContent = `${newQuantity} ×`;
      } else if (qtySpan) {
        qtySpan.remove();
      }

      // Flash animation
      priceEl.classList.add('epl-price-updated');
      setTimeout(() => priceEl.classList.remove('epl-price-updated'), 300);
    }
  }

  /**
   * Update item price display optimistically (before server response)
   * @param {HTMLElement} cartItem - The cart item DOM element
   * @param {number} newQuantity - The new quantity
   */
  updateItemPriceDisplay(cartItem, newQuantity) {
    const unitPrice = parseInt(cartItem.dataset.unitPrice) || 0;
    const newLinePrice = unitPrice * newQuantity;

    // Update final price element
    const priceEl = cartItem.querySelector('.epl-cart-item__final-price');
    if (priceEl) {
      writePrice(priceEl, newLinePrice);
      priceEl.classList.add('epl-price-updated');
      setTimeout(() => priceEl.classList.remove('epl-price-updated'), 300);
    }

    // Update quantity input (ensure it reflects the new value)
    const qtyInput = cartItem.querySelector('.epl-qty-input');
    if (qtyInput) {
      qtyInput.value = newQuantity;
    }

    // Update original price if displayed (for discounts)
    const originalUnitPrice = parseInt(cartItem.dataset.originalUnitPrice) || 0;
    if (originalUnitPrice > unitPrice) {
      const originalLinePrice = originalUnitPrice * newQuantity;
      const originalPriceEl = cartItem.querySelector('.epl-cart-item__price-original');
      if (originalPriceEl) {
        writePrice(originalPriceEl, originalLinePrice);
      }
    }
  }

  /**
   * Verify optimistic price matches server response and correct if needed
   * This handles edge cases like volume discounts that only apply at certain quantities
   * @param {HTMLElement} cartItem - The cart item DOM element
   * @param {Object} serverItem - The item data from server
   * @param {string} currency - Currency code
   */
  verifyItemPrice(cartItem, serverItem, currency) {
    const finalLinePrice = serverItem.final_line_price || serverItem.line_price || 0;
    const priceEl = cartItem.querySelector('.epl-cart-item__final-price');

    if (priceEl) {
      // If server price differs from optimistic price, correct it (EPLE-27: compare the amount
      // written by writePrice, the text may be the ex VAT display)
      if (priceEl.dataset.eplCents !== String(finalLinePrice)) {
        writePrice(priceEl, finalLinePrice, currency);
        priceEl.classList.add('epl-price-updated');
        setTimeout(() => priceEl.classList.remove('epl-price-updated'), 300);
      }
    }

    // Also update original price if there's a discount
    const originalLinePrice = serverItem.original_line_price || finalLinePrice;
    if (originalLinePrice !== finalLinePrice) {
      const originalPriceEl = cartItem.querySelector('.epl-cart-item__price-original');
      if (originalPriceEl) {
        writePrice(originalPriceEl, originalLinePrice, currency);
      }
    }
  }

  /**
   * Update cart footer only (without re-rendering cart items)
   * This is the key optimization - we skip the expensive cart items re-render
   * @param {Object} cartData - Cart data from API (optional, will fetch if not provided)
   */
  async updateCartFooterOnly(cartData = null) {
    const data = cartData || await this.getCartData(true);

    // Fetch only the footer section from server
    try {
      const response = await fetch(`${routes.cart_url}?sections=main-cart-footer-epl`);
      if (!response.ok) {
        throw new Error(`Failed to fetch footer: ${response.status}`);
      }

      const sectionsJson = await response.text();
      const parsedSections = JSON.parse(sectionsJson);

      // Update footer HTML
      const footerSection = document.getElementById('main-cart-footer-epl');
      if (footerSection && parsedSections['main-cart-footer-epl']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(parsedSections['main-cart-footer-epl'], 'text/html');
        const newFooter = doc.querySelector('main-cart-footer-epl');

        if (newFooter) {
          const newContents = newFooter.querySelector('.js-contents');
          const currentContents = footerSection.querySelector('.js-contents');
          const hasContent = newFooter.innerHTML.trim().length > 0 && newContents?.innerHTML.trim().length > 0;

          if (hasContent && currentContents) {
            currentContents.innerHTML = newContents.innerHTML;
            // Re-initialize modal triggers in footer
            if (typeof window.initModalTriggers === 'function') {
              window.initModalTriggers(footerSection);
            }
          } else {
            // Fallback: update totals manually from cart data
            this.updateFooterTotals(footerSection, data);
          }
        }
      }

      // Update empty state classes
      this.classList.toggle('is-empty', data.item_count === 0);
      const footer = document.getElementById('main-cart-footer-epl');
      if (footer) {
        footer.classList.toggle('is-empty', data.item_count === 0);
      }
    } catch (error) {
      console.error('Error updating footer:', error);
      // Fallback: update totals manually
      const footerSection = document.getElementById('main-cart-footer-epl');
      if (footerSection) {
        this.updateFooterTotals(footerSection, data);
      }
    }
  }

  /**
   * Update cart icon bubble count
   * @param {number} itemCount - New item count
   */
  updateCartIconBubble(itemCount) {
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (!cartIcon) return;

    // Try to find and update existing count bubble
    const countBubble = cartIcon.querySelector('.cart-count-bubble span') ||
                        cartIcon.querySelector('.count-bubble');

    if (countBubble) {
      countBubble.textContent = itemCount;
      // Show/hide bubble based on count
      const bubbleContainer = cartIcon.querySelector('.cart-count-bubble');
      if (bubbleContainer) {
        bubbleContainer.style.display = itemCount > 0 ? '' : 'none';
      }
    }
  }

  /**
   * Update the desktop order summary column with new cart totals
   * This updates the .epl-cart-summary-column sidebar on desktop
   * @param {Object} cartData - Cart data from API
   */
  updateSummaryColumn(cartData) {
    const currency = cartData.currency || 'NOK';
    const totalPrice = cartData.total_price || 0;

    // Reconcile the barometer(s) first — this is independent of the desktop
    // summary column (which is display:none on mobile) and is what keeps the
    // mobile footer bar correct on the optimistic quantity path.
    this.updateFreeShippingBar(totalPrice, currency);

    const summaryColumn = this.querySelector('.epl-cart-summary-column');
    if (!summaryColumn) return;

    // Update subtotal
    const subtotalEl = summaryColumn.querySelector('.epl-summary-subtotal');
    if (subtotalEl) {
      writePrice(subtotalEl, totalPrice, currency);
      subtotalEl.classList.add('epl-price-updated');
      setTimeout(() => subtotalEl.classList.remove('epl-price-updated'), 300);
    }

    // Update total
    const totalEl = summaryColumn.querySelector('.epl-summary-total');
    if (totalEl) {
      writePrice(totalEl, totalPrice, currency);
      totalEl.classList.add('epl-price-updated');
      setTimeout(() => totalEl.classList.remove('epl-price-updated'), 300);
    }

    // Update data attribute on order summary container
    const orderSummary = summaryColumn.querySelector('.epl-order-summary');
    if (orderSummary) {
      orderSummary.dataset.cartTotal = totalPrice;
    }

    // Update Klarna placement amount
    const klarnaPlacement = summaryColumn.querySelector('#klarna-nmp-cart-summary');
    if (klarnaPlacement) {
      klarnaPlacement.setAttribute('data-purchase-amount', totalPrice.toString());
      window.Klarna?.OnsiteMessaging?.refresh?.();
    }

    // Note: Savings row is complex (requires iterating all items to recalculate)
    // For optimistic updates, we skip savings recalculation - it will be correct after footer fetch
  }

  /**
   * Recompute the free-shipping barometer(s) for a new cart total (optimistic).
   * Each .epl-fsb container is always present (when enabled) and carries its
   * config as data-* attributes, so we update it in place without re-rendering
   * the section. Updates every .epl-fsb on the page (desktop summary + mobile
   * footer). Mirrors the Liquid in snippets/cart-free-shipping-bar-epl.liquid.
   * @param {number} totalOre - New cart total in øre (cents)
   * @param {string} currency - Currency code
   */
  updateFreeShippingBar(totalOre, currency = 'NOK') {
    document.querySelectorAll('.epl-fsb').forEach((bar) => {
      const thresholdOre = parseInt(bar.dataset.fsbThresholdOre, 10);
      const floorOre = parseInt(bar.dataset.fsbFloorOre, 10);
      if (!thresholdOre) return;

      const textEl = bar.querySelector('.epl-fsb__text');
      const fillEl = bar.querySelector('.epl-fsb__fill');

      bar.classList.remove('epl-fsb--hidden', 'epl-fsb--progress', 'epl-fsb--achieved');

      if (totalOre >= thresholdOre) {
        bar.classList.add('epl-fsb--achieved');
        if (textEl) textEl.textContent = bar.dataset.fsbAchievedText || '';
        if (fillEl) fillEl.style.width = '100%';
      } else if (totalOre > floorOre) {
        bar.classList.add('epl-fsb--progress');
        const remaining = thresholdOre - totalOre;
        const amountHtml = '<strong class="epl-fsb__amount">' + formatMoney(remaining, currency) + '</strong>';
        const template = bar.dataset.fsbProgressText || '';
        // split/join replaces every [amount] (like Liquid's `replace`) and avoids
        // String.replace treating `$&`/`$'` in the amount as special patterns.
        if (textEl) textEl.innerHTML = template.split('[amount]').join(amountHtml);
        // Match Liquid's integer division (total * 100 / thresholdOre)
        const percent = Math.min(100, Math.floor((totalOre * 100) / thresholdOre));
        if (fillEl) fillEl.style.width = percent + '%';
      } else {
        bar.classList.add('epl-fsb--hidden');
      }
    });
  }

  async updateQuantity(itemKey, quantity, oldQuantity = null, currency = null, isAddOn = false) {
    // Get old cart data to compare item count and check for add-ons
    const oldCartData = await fetch(`${routes.cart_url}.js`).then(r => r.json());
    const oldItemCount = oldCartData.item_count;
    const oldItem = oldCartData.items.find(item => item.key === itemKey);
    
    if (!oldItem) {
      throw new Error('Item not found in cart');
    }

    // Check if this is an add-on item (not a main product)
    const isAddOnItem = isAddOn || oldItem.properties?._aplcr_main_product_sku != null || 
                        oldItem.properties?._sw_main_product_sku != null || 
                        oldItem.properties?._vegglader_main_product_sku != null ||
                        oldItem.properties?._gwp_main_product_sku != null;

    // Check if product has add-ons - if so, update add-ons too
    const hasAddOnsForUpdate = !isAddOnItem && this.hasAddOns(itemKey, oldCartData);
    let updates = { [itemKey]: quantity };
    
    if (hasAddOnsForUpdate) {
      // Get all add-on keys for this main product
      const mainItemSku = oldItem.sku;
      const mainItemUuid = oldItem.properties?._uuid || '';
      const addOnKeys = [];
      
      oldCartData.items.forEach(item => {
        if (item.key === itemKey) return; // Skip main item
        
        const isAppleCare = item.properties?._aplcr_main_product_sku === mainItemSku;
        const isSecondWarranty = item.properties?._sw_main_product_sku === mainItemSku;
        const isVegglader = item.properties?._vegglader_main_product_sku === mainItemSku;
        const isGWP = item.properties?._gwp_main_product_sku === mainItemSku;
        
        const uuidMatches = addonUuidMatchesMain(item.properties?._uuid, mainItemUuid);
        
        if ((isAppleCare || isSecondWarranty || isVegglader || isGWP) && uuidMatches) {
          addOnKeys.push(item.key);
        }
      });
      
      // Update add-ons to match the new quantity
      addOnKeys.forEach(key => {
        updates[key] = quantity;
      });
    }
    
    const response = await fetch(`${routes.cart_update_url}`, {
      ...getFetchConfig('POST', { updates }),
    });

    if (!response.ok) {
      throw new Error('Failed to update quantity');
    }

    // Get updated cart data
    const cartData = await fetch(`${routes.cart_url}.js`).then(r => r.json());
    const newItemCount = cartData.item_count;

    // Track quantity change if we have old quantity
    if (oldQuantity !== null && oldQuantity !== quantity) {
      const updatedItem = cartData.items.find(item => item.key === itemKey);
      
      if (updatedItem) {
        const eventType = quantity > oldQuantity ? 'add_to_cart' : 'remove_from_cart';
        const quantityDiff = Math.abs(quantity - oldQuantity);
        const itemPrice = (updatedItem.final_price || updatedItem.price || 0) / 100;
        const value = itemPrice * quantityDiff;
        const trackCurrency = currency || cartData.currency || window.themeJsData?.shopCurrency || 'NOK';
        
        trackCartEvent(eventType, {
          sku: updatedItem.sku,
          variant_id: updatedItem.variant_id,
          product_title: updatedItem.product_title,
          quantity: quantityDiff,
          final_price: updatedItem.final_price || updatedItem.price
        }, trackCurrency, value);
      }
    }

    // Always do full re-render to ensure all quantities, prices, and add-ons are correctly displayed
    await this.updateCart();
  }

  async addUpsell(button, type) {
    // Support both camelCase (from dataset) and kebab-case (from getAttribute)
    const variantId = button.dataset.variantId || button.dataset.variantid || button.getAttribute('data-variant-id');
    const mainProductSku = button.dataset.skuparent || button.dataset.mainProductSku || button.getAttribute('data-main-product-sku') || button.getAttribute('data-skuparent');
    const mainProductHandle = button.dataset.handleparent || button.dataset.mainProductHandle || button.getAttribute('data-main-product-handle') || button.getAttribute('data-handleparent');
    const mainProductUuid = button.dataset.uuid || button.dataset.mainProductUuid || button.getAttribute('data-main-product-uuid') || button.getAttribute('data-uuid');
    const mainItemKey = button.dataset.mainItemKey || button.dataset.keyparent || button.getAttribute('data-main-item-key') || button.getAttribute('data-keyparent');
    const addonSku = button.dataset.addonSku || button.dataset.skuaplcr || button.dataset.skusw || button.getAttribute('data-addon-sku') || button.getAttribute('data-skuaplcr') || button.getAttribute('data-skusw');
    const addonHandle = button.dataset.addonHandle || button.dataset.handleaplcr || button.dataset.handlesw || button.getAttribute('data-addon-handle') || button.getAttribute('data-handleaplcr') || button.getAttribute('data-handlesw');

    if (!variantId) {
      throw new Error('Variant ID not found');
    }

    const cartData = await this.getCartData(true);
    const mainItem = this._findMainProduct(cartData.items, { mainItemKey, mainProductSku, mainProductUuid });

    const quantity = mainItem ? mainItem.quantity : 1;
    const currentUuid = mainItem?.properties?._uuid || mainProductUuid || mainProductHandle;

    if ((type === 'applecare' || type === 'secondwarranty') && mainItem) {
      await this._addBackLinkedAddon({
        variantId, quantity, addonType: type, mainProductSku, mainProductHandle,
        mainItem, currentUuid, addonSku, addonHandle
      });
    } else {
      // Vegglader or no main item found: simple add
      const properties = { _uuid: currentUuid };
      if (type === 'applecare') {
        properties._aplcr_main_product_sku = mainProductSku;
        properties._aplcr_main_product_handle = mainProductHandle;
      } else if (type === 'secondwarranty') {
        properties._sw_main_product_sku = mainProductSku;
        properties._sw_main_product_handle = mainProductHandle;
      } else if (type === 'vegglader') {
        properties._vegglader_main_product_sku = mainProductSku;
        properties._vegglader_main_product_handle = mainProductHandle;
      }

      const response = await fetch(routes.cart_add_url, {
        ...getFetchConfig('POST', {
          items: [{ id: variantId, quantity, properties }]
        })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to add upsell');
      }
    }

    // GA tracking
    const updatedCartData = await this.getCartData(true);
    const addedItem = updatedCartData.items.find(item =>
      item.variant_id === parseInt(variantId) &&
      (type === 'applecare' ? item.properties?._aplcr_main_product_sku === mainProductSku :
       type === 'secondwarranty' ? item.properties?._sw_main_product_sku === mainProductSku :
       type === 'vegglader' ? item.properties?._vegglader_main_product_sku === mainProductSku : false)
    );

    if (addedItem) {
      const currency = updatedCartData.currency || window.themeJsData?.shopCurrency || 'NOK';
      const value = (addedItem.final_line_price || addedItem.line_price || 0) / 100;
      trackCartEvent('add_to_cart', {
        sku: addedItem.sku,
        variant_id: addedItem.variant_id,
        product_title: addedItem.product_title,
        quantity: addedItem.quantity,
        final_price: addedItem.final_line_price || addedItem.line_price
      }, currency, value);
    }
  }

  async removeUpsell(button, type) {
    let itemKey;
    if (type === 'applecare') {
      itemKey = button.dataset.acLineKey;
    } else if (type === 'secondwarranty') {
      itemKey = button.dataset.swLineKey;
    } else if (type === 'vegglader') {
      itemKey = button.dataset.veggladerLineKey;
    }

    if (!itemKey) {
      throw new Error('Item key not found');
    }

    const cartData = await this.getCartData(true);
    const itemToRemove = cartData.items.find(item => item.key === itemKey);
    const mainProductKey = button.dataset.mainProductKey || button.getAttribute('data-main-product-key');
    const mainProductSku = itemToRemove?.properties?._aplcr_main_product_sku ||
      itemToRemove?.properties?._sw_main_product_sku;
    const mainProductUuid = itemToRemove?.properties?._uuid;

    // Remove the add-on
    const response = await fetch(routes.cart_change_url, {
      ...getFetchConfig('POST', { id: itemKey, quantity: 0 })
    });
    if (!response.ok) throw new Error('Failed to remove upsell');

    // For AC+ and SW: clean up main product back-link properties
    if ((type === 'applecare' || type === 'secondwarranty') && mainProductSku) {
      const mainProductItem = this._findMainProduct(cartData.items, {
        mainItemKey: mainProductKey, mainProductSku, mainProductUuid
      });
      if (mainProductItem) {
        await this._removeBackLinkProperties(mainProductItem, type, mainProductSku);
      }
    }

    // Track removal
    if (itemToRemove) {
      const currency = cartData.currency || window.themeJsData?.shopCurrency || 'NOK';
      const value = (itemToRemove.final_line_price || itemToRemove.line_price || 0) / 100;
      trackCartEvent('remove_from_cart', {
        sku: itemToRemove.sku,
        variant_id: itemToRemove.variant_id,
        product_title: itemToRemove.product_title,
        quantity: itemToRemove.quantity,
        final_price: itemToRemove.final_line_price || itemToRemove.line_price
      }, currency, value);
    }
  }

  /**
   * Update price for a single cart item without full re-render
   * @param {string} itemKey - The cart item key
   * @param {Object} cartData - Updated cart data from API
   */
  updateItemPrice(itemKey, cartData) {
    const cartItem = this.querySelector(`[data-item-key="${itemKey}"]`);
    if (!cartItem) {
      console.warn(`Cart item with key ${itemKey} not found`);
      return;
    }

    const updatedItem = cartData.items.find(item => item.key === itemKey);
    if (!updatedItem) {
      console.warn(`Item ${itemKey} not found in cart data`);
      return;
    }

    const currency = cartData.currency || 'NOK';
    const finalLinePrice = updatedItem.final_line_price || updatedItem.line_price || 0;
    const originalLinePrice = updatedItem.original_line_price || finalLinePrice;
    const hasDiscount = originalLinePrice !== finalLinePrice;

    // Update final price
    const finalPriceElement = cartItem.querySelector('.epl-cart-item__final-price');
    if (finalPriceElement) {
      writePrice(finalPriceElement, finalLinePrice, currency);
    }

    // Handle discounted prices wrapper
    const priceWrapper = cartItem.querySelector('.epl-cart-item__price');
    if (priceWrapper) {
      const discountedPricesWrapper = priceWrapper.querySelector('.epl-cart-item__discounted-prices');
      const oldPriceElement = priceWrapper.querySelector('.epl-cart-item__old-price');

      if (hasDiscount) {
        // Show discounted prices wrapper
        if (!discountedPricesWrapper) {
          // Create discounted prices wrapper if it doesn't exist
          const newWrapper = document.createElement('div');
          newWrapper.className = 'epl-cart-item__discounted-prices';
          
          const visuallyHiddenRegular = document.createElement('span');
          visuallyHiddenRegular.className = 'visually-hidden';
          visuallyHiddenRegular.textContent = 'products.product.price.regular_price';
          
          const oldPrice = document.createElement('s');
          oldPrice.className = 'epl-cart-item__old-price';
          writePrice(oldPrice, originalLinePrice, currency);
          
          const visuallyHiddenSale = document.createElement('span');
          visuallyHiddenSale.className = 'visually-hidden';
          visuallyHiddenSale.textContent = 'products.product.price.sale_price';
          
          if (finalPriceElement) {
            // Clone the final price element to preserve its structure
            const clonedFinalPrice = finalPriceElement.cloneNode(true);
            writePrice(clonedFinalPrice, finalLinePrice, currency);
            
            newWrapper.appendChild(visuallyHiddenRegular);
            newWrapper.appendChild(oldPrice);
            newWrapper.appendChild(visuallyHiddenSale);
            newWrapper.appendChild(clonedFinalPrice);
            priceWrapper.innerHTML = '';
            priceWrapper.appendChild(newWrapper);
          }
        } else {
          // Update existing old price and final price
          if (oldPriceElement) {
            writePrice(oldPriceElement, originalLinePrice, currency);
          }
          if (finalPriceElement) {
            writePrice(finalPriceElement, finalLinePrice, currency);
          }
        }
      } else {
        // Remove discounted prices wrapper if no discount
        if (discountedPricesWrapper) {
          // Create new final price element outside wrapper
          if (finalPriceElement) {
            const newFinalPrice = document.createElement('span');
            newFinalPrice.className = 'epl-cart-item__final-price';
            writePrice(newFinalPrice, finalLinePrice, currency);
            priceWrapper.innerHTML = '';
            priceWrapper.appendChild(newFinalPrice);
          }
        }
      }
    }

    // Remove pulse effect
    this.stopPriceLoading(cartItem);
  }

  /**
   * Update footer totals manually from cart data
   * @param {HTMLElement} footerSection - The footer section element
   * @param {Object} cartData - Cart data with totals
   */
  updateFooterTotals(footerSection, cartData) {
    const currency = cartData.currency || 'NOK';
    const totalPrice = cartData.total_price || 0;
    
    // Update subtotal value
    const subtotalValue = footerSection.querySelector('.epl-cart-footer__subtotal-value');
    if (subtotalValue) {
      writePrice(subtotalValue, totalPrice, currency);
    }
    
  }

  /**
   * Update cart footer totals without re-rendering cart items
   * @param {Object} cartData - Updated cart data from API
   */
  async updateCartFooter(cartData) {
    try {
      // Fetch only the footer section
      const response = await fetch(`${routes.cart_url}?sections=main-cart-footer-epl`);
      if (!response.ok) {
        throw new Error(`Failed to fetch footer: ${response.status}`);
      }

      const html = await response.text();
      const parsedSections = JSON.parse(html);

      // Update footer
      const footerSection = document.getElementById('main-cart-footer-epl');
      if (footerSection && parsedSections['main-cart-footer-epl']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(parsedSections['main-cart-footer-epl'], 'text/html');
        const newFooter = doc.querySelector('main-cart-footer-epl');
        if (newFooter) {
          // Update the .js-contents div which contains the totals
          const newContents = newFooter.querySelector('.js-contents');
          const currentContents = footerSection.querySelector('.js-contents');
          const newFooterHasContent = newFooter.innerHTML.trim().length > 0 && newContents && newContents.innerHTML.trim().length > 0;
          // Only update if new footer has actual content, otherwise skip update to preserve existing content
          if (newFooterHasContent && currentContents) {
            if (newContents && newContents.innerHTML.trim().length > 0) {
              // Update only the .js-contents div
              currentContents.innerHTML = newContents.innerHTML;
            } else {
              // Fallback: update entire footer (newContents missing but newFooter has content)
              footerSection.innerHTML = newFooter.innerHTML;
            }
          } else {
            // Skip HTML update - new footer is empty, but update totals manually from cart data
            this.updateFooterTotals(footerSection, cartData);
          }
          // Refresh Klarna placements after footer re-render
          window.Klarna?.OnsiteMessaging?.refresh?.();
        }
      }

      // Update empty state
      this.classList.toggle('is-empty', cartData.item_count === 0);
      const footer = document.getElementById('main-cart-footer-epl');
      if (footer) {
        footer.classList.toggle('is-empty', cartData.item_count === 0);
      }

      // Update cart icon bubble if needed
      const cartIconResponse = await fetch(`${routes.cart_url}?sections=cart-icon-bubble`);
      if (cartIconResponse.ok) {
        const cartIconHtml = await cartIconResponse.text();
        const cartIconSections = JSON.parse(cartIconHtml);
        if (cartIconSections['cart-icon-bubble']) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(cartIconSections['cart-icon-bubble'], 'text/html');
          const cartIcon = document.getElementById('cart-icon-bubble');
          if (cartIcon) {
            const sectionContent = doc.body.innerHTML || doc.documentElement.innerHTML;
            if (sectionContent) {
              cartIcon.innerHTML = sectionContent;
            }
          }
        }
      }
    } catch (error) {
      console.error('Error updating cart footer:', error);
      // Fallback to full update if footer update fails
      throw error;
    }
  }

  /**
   * Disable the checkout buttons while a cart-modifying operation is in flight.
   * Counter-based so nested/overlapping ops don't prematurely re-enable.
   * @param {boolean} pending - true to increment (disable), false to decrement
   */
  _setCheckoutPending(pending) {
    if (typeof this._checkoutPendingCount !== 'number') this._checkoutPendingCount = 0;
    this._checkoutPendingCount += pending ? 1 : -1;
    if (this._checkoutPendingCount < 0) this._checkoutPendingCount = 0;
    this._applyCheckoutPendingState();
  }

  /**
   * Apply the current checkout-pending state to whatever checkout buttons
   * currently exist in the DOM. Safe to call after any cart re-render so
   * freshly rendered buttons pick up the disabled state while an op is
   * still in flight.
   */
  _applyCheckoutPendingState() {
    const disabled = (this._checkoutPendingCount || 0) > 0;
    document.querySelectorAll('.epl-cart-footer__checkout-button, .epl-checkout-button').forEach(btn => {
      btn.disabled = disabled;
      btn.setAttribute('aria-busy', disabled ? 'true' : 'false');
    });
  }

  /**
   * Apply a pre-fetched sections payload (from /cart/add.js?sections=... or
   * similar) to the DOM in place of a separate /cart?sections=... fetch.
   * Replaces main-cart-items-epl, main-cart-footer-epl and cart-icon-bubble
   * and re-initialises the listeners those sections depend on.
   *
   * @param {Object} parsedSections - map of section id → HTML string
   * @param {Object} [cartData] - optional cart data for empty-state toggles
   */
  async _applyCartSections(parsedSections, cartData = null) {
    const itemsSection = document.getElementById('main-cart-items-epl');
    if (itemsSection && parsedSections['main-cart-items-epl']) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(parsedSections['main-cart-items-epl'], 'text/html');
      const newContent = doc.querySelector('cart-items-epl');
      if (newContent) {
        itemsSection.innerHTML = newContent.innerHTML;

        const priceElements = itemsSection.querySelectorAll('.epl-cart-item__final-price');
        priceElements.forEach(priceEl => priceEl.classList.remove('epl-price-updating'));
        const nmpElements = itemsSection.querySelectorAll('.net-monthly-pricing');
        nmpElements.forEach(nmpEl => nmpEl.classList.remove('epl-price-updating'));

        this.setupAddonToggles();
        this.setupUpsellButtons();
        this.reinitializeModalTriggers();
        this.reinitializePickupAvailability();
      }
    }

    const footerSection = document.getElementById('main-cart-footer-epl');
    if (footerSection && parsedSections['main-cart-footer-epl']) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(parsedSections['main-cart-footer-epl'], 'text/html');
      const newFooter = doc.querySelector('main-cart-footer-epl');
      if (newFooter) {
        const newContents = newFooter.querySelector('.js-contents');
        const currentContents = footerSection.querySelector('.js-contents');
        const hasContent = newFooter.innerHTML.trim().length > 0 && newContents && newContents.innerHTML.trim().length > 0;
        if (hasContent && currentContents) {
          currentContents.innerHTML = newContents.innerHTML;
          if (typeof window.initModalTriggers === 'function') {
            window.initModalTriggers(footerSection);
          }
        } else if (cartData) {
          this.updateFooterTotals(footerSection, cartData);
        }
      }
    }

    if (parsedSections['cart-icon-bubble']) {
      const cartIcon = document.getElementById('cart-icon-bubble');
      if (cartIcon) {
        const sectionHtml = parsedSections['cart-icon-bubble'];
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHtml, 'text/html');
        let sectionContent = doc.body?.innerHTML || doc.documentElement?.innerHTML;
        if (!sectionContent || sectionContent.trim().length === 0) {
          sectionContent = sectionHtml;
        }
        if (sectionContent && sectionContent.trim().length > 0) {
          cartIcon.innerHTML = sectionContent;
        }
      }
    }

    const data = cartData || await this.getCartData(true);
    this.classList.toggle('is-empty', data.item_count === 0);
    const footer = document.getElementById('main-cart-footer-epl');
    if (footer) {
      footer.classList.toggle('is-empty', data.item_count === 0);
    }

    // Reconcile the free-shipping barometer(s) — the footer bar's block is
    // returned empty by the Section Rendering API, so the footer swap above
    // never re-renders it. See updateCart() for the full explanation.
    this.updateFreeShippingBar(data.total_price, data.currency || 'NOK');

    // Re-apply pending state so freshly rendered buttons stay disabled until
    // the outer op decrements the counter.
    this._applyCheckoutPendingState();
  }

  async updateCart() {
    const sections = ['main-cart-items-epl', 'main-cart-footer-epl', 'cart-icon-bubble'];
    const sectionsQuery = sections.join(',');

    try {
      const response = await fetch(`${routes.cart_url}?sections=${sectionsQuery}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }
      const html = await response.text();
      const parsedSections = JSON.parse(html);
      
      // Update cart items
      const itemsSection = document.getElementById('main-cart-items-epl');
      if (itemsSection && parsedSections['main-cart-items-epl']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(parsedSections['main-cart-items-epl'], 'text/html');
        const newContent = doc.querySelector('cart-items-epl');
        if (newContent) {
          itemsSection.innerHTML = newContent.innerHTML;
          // Custom elements should automatically upgrade when innerHTML is replaced
          // The browser will call connectedCallback() automatically for new custom elements
          // No need to manually call it, as that would cause duplicate event listeners

          // Remove any remaining pulse effects after cart update
          const priceElements = itemsSection.querySelectorAll('.epl-cart-item__final-price');
          priceElements.forEach(priceEl => {
            priceEl.classList.remove('epl-price-updating');
          });
          const nmpElements = itemsSection.querySelectorAll('.net-monthly-pricing');
          nmpElements.forEach(nmpEl => {
            nmpEl.classList.remove('epl-price-updating');
          });

          // Re-initialize addon toggles and upsell buttons after DOM update
          this.setupAddonToggles();
          this.setupUpsellButtons();

          // Re-initialize modal triggers after DOM update
          this.reinitializeModalTriggers();

          // Re-initialize pickup availability after DOM update
          this.reinitializePickupAvailability();
        }
      }

      // Update footer - update the .js-contents div inside main-cart-footer-epl
      const footerSection = document.getElementById('main-cart-footer-epl');
      if (footerSection && parsedSections['main-cart-footer-epl']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(parsedSections['main-cart-footer-epl'], 'text/html');
        const newFooter = doc.querySelector('main-cart-footer-epl');
        if (newFooter) {
          // Update the .js-contents div which contains the totals
          const newContents = newFooter.querySelector('.js-contents');
          const currentContents = footerSection.querySelector('.js-contents');
          const newFooterHasContent = newFooter.innerHTML.trim().length > 0 && newContents && newContents.innerHTML.trim().length > 0;
          // Only update if new footer has actual content, otherwise skip update to preserve existing content
          if (newFooterHasContent && currentContents) {
            if (newContents && newContents.innerHTML.trim().length > 0) {
              // Update only the .js-contents div
              currentContents.innerHTML = newContents.innerHTML;
            } else {
              // Fallback: update entire footer (newContents missing but newFooter has content)
              footerSection.innerHTML = newFooter.innerHTML;
            }
            // Re-initialize modal triggers in the updated footer
            if (typeof window.initModalTriggers === 'function') {
              window.initModalTriggers(footerSection);
            }
          } else {
            // Skip HTML update - new footer is empty, but update totals manually from cart data
            // Fetch cart data to get updated totals
            const cartData = await fetch(`${routes.cart_url}.js`).then(r => r.json());
            this.updateFooterTotals(footerSection, cartData);
          }
        }
      }

      // Update cart icon bubble - the section returns content that goes inside #cart-icon-bubble
      if (parsedSections['cart-icon-bubble']) {
        const cartIcon = document.getElementById('cart-icon-bubble');
        if (cartIcon) {
          // The section returns HTML content directly as a string
          const sectionHtml = parsedSections['cart-icon-bubble'];
          
          // Parse the HTML to extract the content
          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionHtml, 'text/html');
          
          // Try to get content from body first, then documentElement
          let sectionContent = doc.body?.innerHTML || doc.documentElement?.innerHTML;
          
          // If that doesn't work, try getting the text content and reconstruct
          if (!sectionContent || sectionContent.trim().length === 0) {
            // The section might be wrapped differently, try using the raw HTML
            sectionContent = sectionHtml;
          }
          
          if (sectionContent && sectionContent.trim().length > 0) {
            cartIcon.innerHTML = sectionContent;
          } else {
            // Fallback: update just the count bubble if it exists
            const cartData = await fetch(`${routes.cart_url}.js`).then(r => r.json());
            const countBubble = cartIcon.querySelector('.cart-count-bubble .count-bubble');
            if (countBubble) {
              countBubble.textContent = cartData.item_count;
            } else if (cartData.item_count > 0) {
              // Create count bubble if it doesn't exist
              const countBubbleDiv = cartIcon.querySelector('.cart-count-bubble');
              if (countBubbleDiv) {
                const countSpan = document.createElement('span');
                countSpan.className = 'count-bubble';
                countSpan.setAttribute('aria-hidden', 'true');
                countSpan.textContent = cartData.item_count;
                countBubbleDiv.insertBefore(countSpan, countBubbleDiv.firstChild);
              }
            }
          }
        }
      }

      // Update empty state
      const cartData = await fetch(`${routes.cart_url}.js`).then(r => r.json());
      this.classList.toggle('is-empty', cartData.item_count === 0);
      const footer = document.getElementById('main-cart-footer-epl');
      if (footer) {
        footer.classList.toggle('is-empty', cartData.item_count === 0);
      }

      // Reconcile the free-shipping barometer(s) from the fresh cart total.
      // The footer bar lives inside the footer's `subtotal` block, which the
      // Section Rendering API returns empty (the footer swap above no-ops to
      // updateFooterTotals), so the server never re-renders the footer bar.
      // updateFreeShippingBar recomputes every .epl-fsb on the page, so this is
      // what keeps the mobile footer bar correct on remove/full-refresh paths.
      this.updateFreeShippingBar(cartData.total_price, cartData.currency || 'NOK');

      // Re-apply pending state so freshly rendered checkout buttons stay
      // disabled if an outer op is still in flight.
      this._applyCheckoutPendingState();

      // Trigger custom event for other scripts
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  }

  enableLoading(index) {
    // Only used for non-quantity operations (remove, upsell)
    const item = this.querySelector(`#CartItem-${index}`);
    if (item) {
      const overlay = item.querySelector('.loading-overlay');
      if (overlay) {
        overlay.classList.remove('hidden');
      }
      item.classList.add('is-loading');
    }
    this.classList.add('cart-items--disabled');
  }

  disableLoading(index) {
    // Only used for non-quantity operations (remove, upsell)
    const item = this.querySelector(`#CartItem-${index}`);
    if (item) {
      const overlay = item.querySelector('.loading-overlay');
      if (overlay) {
        overlay.classList.add('hidden');
      }
      item.classList.remove('is-loading');
    }
    this.classList.remove('cart-items--disabled');
  }

  disableButtons() {
    // Only used for non-quantity operations (remove, upsell)
    const buttons = this.querySelectorAll('button, .epl-cart-drawer-button');
    buttons.forEach(button => {
      button.disabled = true;
      button.classList.add('force-disabled');
    });
  }

  enableButtons() {
    // Only used for non-quantity operations (remove, upsell)
    const buttons = this.querySelectorAll('button, .epl-cart-drawer-button');
    buttons.forEach(button => {
      button.disabled = false;
      button.classList.remove('force-disabled');
    });
  }

  showError(message) {
    const errorContainer = document.getElementById('epl-cart-errors');
    if (errorContainer) {
      errorContainer.textContent = message;
      errorContainer.setAttribute('role', 'alert');
      setTimeout(() => {
        errorContainer.textContent = '';
        errorContainer.removeAttribute('role');
      }, 5000);
    }
  }

  /**
   * Re-initialize modal triggers after cart DOM update
   * When innerHTML is replaced, modal trigger buttons lose their event listeners.
   * This method calls the global initModalTriggers function to re-attach listeners.
   */
  reinitializeModalTriggers() {
    if (typeof window.initModalTriggers === 'function') {
      window.initModalTriggers(this);
    }
  }

  /**
   * Re-initialize pickup availability elements after cart DOM update
   * When innerHTML is replaced, custom elements like <pickup-availability> are destroyed
   * and recreated, but the parent <my-store-locator> doesn't know about the new elements.
   * This method triggers a re-fetch of availability data.
   */
  reinitializePickupAvailability() {
    const storeLocator = document.querySelector('my-store-locator');
    if (storeLocator && typeof storeLocator.fetchLocations === 'function') {
      // Get current position from cookie if available
      let position = null;
      try {
        const positionCookie = document.cookie
          .split('; ')
          .find(row => row.startsWith('location_position='));
        if (positionCookie) {
          position = JSON.parse(decodeURIComponent(positionCookie.split('=')[1]));
        }
      } catch (e) {
        // Ignore cookie parse errors
      }

      // fetchLocations will query all current pickup-availability elements,
      // fetch their availability data, and call renderPreview on them
      storeLocator.fetchLocations(position, false, () => {
        // Callback after fetch completes - availability data is now rendered
      });
    }
  }

  /**
   * Check if a product has associated add-ons (AC+, SW, Vegglader, GWP)
   * @param {string} itemKey - The cart item key
   * @param {Object} cartData - Cart data from API
   * @returns {boolean} True if product has add-ons
   */
  hasAddOns(itemKey, cartData) {
    const mainItem = cartData.items.find(item => item.key === itemKey);
    if (!mainItem) return false;

    const mainItemSku = mainItem.sku;
    const mainItemUuid = mainItem.properties?._uuid || '';

    // Check if any items have this main product as their parent
    return cartData.items.some(item => {
      if (item.key === itemKey) return false; // Skip the main item itself
      
      const isAppleCare = item.properties?._aplcr_main_product_sku === mainItemSku;
      const isSecondWarranty = item.properties?._sw_main_product_sku === mainItemSku;
      const isVegglader = item.properties?._vegglader_main_product_sku === mainItemSku;
      const isGWP = item.properties?._gwp_main_product_sku === mainItemSku;
      
      const uuidMatches = addonUuidMatchesMain(item.properties?._uuid, mainItemUuid);
      
      return (isAppleCare || isSecondWarranty || isVegglader || isGWP) && uuidMatches;
    });
  }


  /**
   * Show full viewport loading overlay
   */
  showLoadingOverlay() {
    const overlay = document.getElementById('epl-cart-loading-overlay');
    if (overlay) {
      overlay.classList.add('active');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
  }

  /**
   * Hide full viewport loading overlay
   */
  hideLoadingOverlay() {
    const overlay = document.getElementById('epl-cart-loading-overlay');
    if (overlay) {
      overlay.classList.remove('active');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  }

  /**
   * Show a notification message to the user
   * @param {string} message - The notification message
   * @param {number} duration - Duration in milliseconds (default: 4000)
   */
  showNotification(message, duration = 4000) {
    const errorContainer = document.getElementById('epl-cart-errors');
    if (errorContainer) {
      errorContainer.textContent = message;
      errorContainer.setAttribute('role', 'status');
      errorContainer.classList.add('epl-cart-notification');
      setTimeout(() => {
        errorContainer.textContent = '';
        errorContainer.removeAttribute('role');
        errorContainer.classList.remove('epl-cart-notification');
      }, duration);
    }
  }

}

if (!customElements.get('cart-items-epl')) {
  customElements.define('cart-items-epl', CartItemsEpl);
}

// ============================================================================
// Cart Remove Button Custom Element
// ============================================================================

class CartRemoveButtonEpl extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick.bind(this));
  }

  async handleClick(event) {
    event.preventDefault();
    const cartItems = this.closest('cart-items-epl');
    if (!cartItems) return;

    const index = this.dataset.index;
    const itemKey = this.dataset.itemKey;
    // Capture the row container synchronously, before any await. A concurrent
    // add-on toggle re-renders the cart items and detaches this button/row;
    // reading the container after an await could yield null and make the remove
    // silently bail. The detached node still exposes data-sku for the fallback.
    const cartItem = this.closest('.epl-cart-item');

    if (this.classList.contains('force-disabled')) {
      return;
    }

    // Show full viewport loading overlay
    cartItems.showLoadingOverlay();
    cartItems.enableLoading(index);
    cartItems.disableButtons();

    try {
      // Use operation lock to prevent race conditions
      await cartItems.executeWithLock(async () => {
        // Get fresh item data inside lock (may have changed if operation was queued)
        const cartData = await cartItems.getCartData(true);

        // The cached data-item-key rotates after add-on property mutations; resolve
        // the live line (and self-heal the attribute) instead of silently bailing —
        // that stale-key bail is what made the first remove a no-op after adding AC+.
        // cartItem was captured synchronously above so a concurrent re-render
        // can't yank it out from under us mid-operation.
        const itemToRemove = cartItems._resolveMainItem(cartItem, itemKey, cartData);

        // If item no longer exists, skip (already removed by another operation)
        if (!itemToRemove) {
          console.warn('Item no longer exists in cart, skipping removal');
          return;
        }

        const resolvedKey = itemToRemove.key;

        // Cascade-remove any linked add-ons. Derive this from the actual cart
        // state (fresh cartData) rather than the cached data-typeofbutton flag,
        // which can go stale and would otherwise orphan add-ons (or skip the
        // cascade) when the flag and reality disagree.
        const mainItemSku = itemToRemove.sku || '';
        const mainItemUuid = itemToRemove.properties?._uuid || '';
        const addOnKeys = await this.getAddOnKeys(resolvedKey, mainItemSku, mainItemUuid, cartData);

        if (addOnKeys.length > 0) {
          await this.removeMainProduct(resolvedKey, addOnKeys);
        } else {
          await this.removeItem(resolvedKey);
        }

        // Track removal
        const currency = cartData.currency || window.themeJsData?.shopCurrency || 'NOK';
        const value = (itemToRemove.final_line_price || itemToRemove.line_price || 0) / 100;
        trackCartEvent('remove_from_cart', {
          sku: itemToRemove.sku,
          variant_id: itemToRemove.variant_id,
          product_title: itemToRemove.product_title,
          quantity: itemToRemove.quantity,
          final_price: itemToRemove.final_line_price || itemToRemove.line_price
        }, currency, value);

        // Invalidate cache after removal
        cartItems.invalidateCartCache();
      });

      await cartItems.updateCart();
    } catch (error) {
      console.error('Error removing item:', error);
      cartItems.showError('Kunne ikke fjerne varen. Prøv igjen.');
    } finally {
      cartItems.disableLoading(index);
      cartItems.enableButtons();
      // Hide loading overlay
      cartItems.hideLoadingOverlay();
    }
  }

  async getAddOnKeys(mainItemKey, mainItemSku, mainItemUuid, cartData = null) {
    const addOnKeys = [];

    // Use already-fetched cart data when provided; otherwise fetch
    // (add-ons are not rendered in the DOM, so we match them by properties).
    const data = cartData || await fetch(`${routes.cart_url}.js`).then(r => r.json());

    // Find add-ons by checking properties
    data.items.forEach(item => {
      const isAppleCare = item.properties?._aplcr_main_product_sku === mainItemSku;
      const isSecondWarranty = item.properties?._sw_main_product_sku === mainItemSku;
      const isVegglader = item.properties?._vegglader_main_product_sku === mainItemSku;
      const isGWP = item.properties?._gwp_main_product_sku === mainItemSku;
      
      // Match by UUID if available to ensure we get the right add-ons for this specific main product instance
      const uuidMatches = addonUuidMatchesMain(item.properties?._uuid, mainItemUuid);
      
      if ((isAppleCare || isSecondWarranty || isVegglader || isGWP) && uuidMatches) {
        addOnKeys.push(item.key);
      }
    });
    
    return addOnKeys;
  }

  async removeItem(itemKey) {
    const response = await fetch(`${routes.cart_update_url}`, {
      ...getFetchConfig('POST', {
        updates: { [itemKey]: 0 },
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to remove item');
    }
  }

  async removeMainProduct(mainKey, addOnKeys) {
    const updates = { [mainKey]: 0 };
    
    // Add all add-on keys to updates
    addOnKeys.forEach(key => {
      updates[key] = 0;
    });

    const response = await fetch(`${routes.cart_update_url}`, {
      ...getFetchConfig('POST', { updates }),
    });

    if (!response.ok) {
      throw new Error('Failed to remove main product');
    }
  }
}

if (!customElements.get('cart-remove-button-epl')) {
  customElements.define('cart-remove-button-epl', CartRemoveButtonEpl);
}

// ============================================================================
// Cart Drawer Items Custom Element
// ============================================================================

class CartDrawerItemsEpl extends CartItemsEpl {
  constructor() {
    super();
  }

  /**
   * Override updateCart to handle drawer-specific updates
   */
  async updateCart() {
    try {
      // Fetch cart-drawer-epl section
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer-epl`);
      if (!response.ok) {
        throw new Error(`Failed to fetch cart drawer: ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      
      // Update cart-drawer-items element
      const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
      for (const selector of selectors) {
        const targetElement = document.querySelector(selector);
        const sourceElement = html.querySelector(selector);
        if (targetElement && sourceElement) {
          targetElement.replaceWith(sourceElement);
        }
      }

      // Re-initialize modal triggers after DOM update
      const newCartDrawerItems = document.querySelector('cart-drawer-items');
      if (newCartDrawerItems && typeof window.initModalTriggers === 'function') {
        window.initModalTriggers(newCartDrawerItems);
      }

      // Update cart icon bubble
      const cartIconSection = html.querySelector('#cart-icon-bubble');
      if (cartIconSection) {
        const currentCartIcon = document.getElementById('cart-icon-bubble');
        if (currentCartIcon) {
          currentCartIcon.innerHTML = cartIconSection.innerHTML;
        }
      }

      // Update empty state
      const cartData = await fetch(`${routes.cart_url}.js`).then(r => r.json());
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        drawer.classList.toggle('is-empty', cartData.item_count === 0);
      }

      // Trigger custom event for other scripts
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));
    } catch (error) {
      console.error('Error updating cart drawer:', error);
      throw error;
    }
  }
}

if (!customElements.get('cart-drawer-items-epl')) {
  customElements.define('cart-drawer-items-epl', CartDrawerItemsEpl);
}


})(); // End of IIFE and __EPL_CART_LOADED__ check
